import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import {
  FeatherX,
  FeatherChevronDown,
  FeatherFile,
  FeatherDownload,
  FeatherEye,
  FeatherAlertCircle,
} from "@subframe/core";
import { ApprovalEntity } from "../../domain/entities/ApprovalEntity";
import { useInvoiceDetails } from "../../../invoices/presentation/hooks/useInvoiceDetails";
import { useBillDetails } from "../../../bills/presentation/hooks/useBillDetails";
import { getSignedUrlForDocument } from "../../../../services/api";
import { formatDateDDMMYYYY } from "../../../../shared/utils/formatters";
import {
  formatJournalChangesForBanner,
  formatCustomerDetails,
  formatVendorDetails,
  formatAllLineItemsFromRawJson,
  formatCreditDebitNoteHeaderFromRawJson,
  formatInvoiceBillCreateSummaryFromRawJson,
  formatRecurringJournalFieldsOnly,
  hasRecurringParamsInRawJson,
} from "../../../../shared/presentation/components/ReviewRequestModal";
import { InvoiceBillFinancialTotals } from "../../../../shared/presentation/components/InvoiceBillFinancialTotals";
import { useResolvedVendorGst } from "../../../../shared/presentation/hooks/useResolvedVendorGst";
import { ValidationChecks } from "./ValidationChecks";
import { getCompliance } from "../../../compliance/data/api";
import { ComplianceProofEntity } from "../../../compliance/domain/entities/TenantComplianceEntity";
import { getBankAccountApprovalTitle } from "../../../../shared/utils/bankAccountApprovalUi";
import type { TaxEntity } from "../../../../shared/domain/entities/TaxEntity";
import {
  formatInvoiceBillFieldChangesForApprovalList,
  formatInvoiceBillReviewChangesForApprovalDisplay,
} from "../../../../shared/utils/review";
import {
  formatCombinedActionTypes,
  getReviewActionLabels,
  sortActionTypesForDisplay,
} from "../../../../shared/utils/review/reviewActionLabels";
import { getStoredAccountingBackend } from "../../../../config/accounting";
import { useTaxes } from "../../../../shared/presentation/hooks/useTaxes";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import {
  buildInvoiceBillBeforeRequestedEditsJson,
  getLineItemDisplayTotal,
  getLineItemInclusiveTotal,
  getLineItemTaxAmount,
  getLineItemTaxRowLabel,
  getValidationSnapshotLineItems,
  shouldShowLineItemTaxRow,
} from "../utils/approvalLineItemDisplay";
import { BulkUploadReviewRowsModal } from "./BulkUploadReviewRowsModal";

interface ApprovalDetailViewPanelProps {
  approval: ApprovalEntity | null;
  onClose: () => void;
}

const formatAmount = (
  rawJson: any,
  entityType?: string,
  changeDetails?: any,
): string => {
  if (!rawJson) return "N/A";

  // Customer and vendor entities don't have amounts
  if (entityType === "customer" || entityType === "vendor") {
    return "";
  }

  // For credit notes, use original_credit_note.credit_note.json.total if available
  if (entityType === "credit_note" && changeDetails?.original_credit_note) {
    const originalCreditNote = changeDetails.original_credit_note.credit_note;
    if (originalCreditNote?.json) {
      const creditNoteJson =
        typeof originalCreditNote.json === "string"
          ? JSON.parse(originalCreditNote.json)
          : originalCreditNote.json;
      const amount = creditNoteJson.total || 0;
      const currencySymbol = creditNoteJson.currency_symbol || "₹";
      return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  }

  // For bank transactions, use categorize_in_zoho_json if available
  if (entityType === "bank_transaction" && rawJson.categorize_in_zoho_json) {
    const categorizeData = rawJson.categorize_in_zoho_json;
    const amount = categorizeData.amount || rawJson.amount || 0;
    const currencySymbol =
      categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
    return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  const amount =
    rawJson.total ||
    rawJson.amount ||
    rawJson.sub_total ||
    rawJson.total_amount ||
    0;
  const currencySymbol = rawJson.currency_symbol || "USD";
  return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);

    // Format as DD-MM-YYYY
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

const getReferenceNumber = (rawJson: any, entityType: string): string => {
  if (!rawJson) return "N/A";
  switch (entityType) {
    case "invoice":
      return rawJson.invoice_number || "N/A";
    case "bill":
      return rawJson.bill_number || rawJson.vendor_bill_number || "N/A";
    case "customer":
      return rawJson.customer_name || rawJson.customerid || "N/A";
    case "vendor":
      return (
        rawJson.vendor_legal_name ||
        rawJson.vendor_name ||
        rawJson.vendorid ||
        "N/A"
      );
    case "credit_note":
      return (
        rawJson.creditnote_number ||
        rawJson.credit_note_number ||
        rawJson.reference_number ||
        "N/A"
      );
    case "debit_note":
      return (
        rawJson.debitnote_number ||
        rawJson.debit_note_number ||
        rawJson.reference_number ||
        "N/A"
      );
    case "journal":
    case "ap_ar_journal":
      return rawJson.journal_number || rawJson.reference_number || "N/A";
    case "bank_transaction":
      return rawJson.transaction_id || rawJson.reference_number || "N/A";
    default:
      return "N/A";
  }
};

const getCustomerOrVendorName = (rawJson: any, entityType: string): string => {
  if (!rawJson) return "N/A";
  if (entityType === "customer") {
    return rawJson.customer_name || "N/A";
  }
  if (entityType === "vendor") {
    return rawJson.vendor_legal_name || rawJson.vendor_name || "N/A";
  }
  if (entityType === "invoice" || entityType === "credit_note") {
    return rawJson.customer_name || "N/A";
  }
  if (entityType === "bill" || entityType === "debit_note") {
    return rawJson.vendor_name || "N/A";
  }
  return "N/A";
};

const getApprovalEntityDisplayName = (
  entityType: string,
  context: "badge" | "header" = "header",
): string => {
  const entityLabels: Record<string, string> = {
    invoice: "Invoice",
    bill: "Bill",
    customer: "Customer",
    vendor: "Vendor",
    credit_note: "Credit Note",
    debit_note: "Debit Note",
    journal: "Journal",
    ap_ar_journal: "AP/AR Journal",
    journal_template:
      context === "badge" ? "Recurring Journal" : "Recurring Journal Template",
    bank_transaction: "Bank Transaction",
    bank_account: "Bank Account",
    gl: "General Ledger",
    month_end_checklist_item: "Month End Checklist",
    tenant_compliance_calendar: "Compliance",
  };

  return entityLabels[entityType] || formatValue(entityType);
};

/**
 * Calculate total debit and credit from journal line items
 */
const calculateJournalTotals = (
  lineItems: any[],
): {
  totalDebit: number;
  totalCredit: number;
} => {
  let totalDebit = 0;
  let totalCredit = 0;

  if (!lineItems || !Array.isArray(lineItems)) {
    return { totalDebit: 0, totalCredit: 0 };
  }

  lineItems.forEach((item) => {
    const amount = parseFloat(item.amount || item.debit || item.credit || 0);
    const isDebitOrCredit = item.is_debit_or_credit?.toLowerCase();

    if (isDebitOrCredit === "debit") {
      totalDebit += amount;
    } else if (isDebitOrCredit === "credit") {
      totalCredit += amount;
    } else {
      // Fallback: if no is_debit_or_credit, check debit/credit fields
      if (item.debit && parseFloat(item.debit) > 0) {
        totalDebit += parseFloat(item.debit);
      }
      if (item.credit && parseFloat(item.credit) > 0) {
        totalCredit += parseFloat(item.credit);
      }
    }
  });

  return { totalDebit, totalCredit };
};

/**
 * Format journal information for display
 */
const formatJournalInfo = (
  originalJson: any,
  rawJson?: any,
): {
  journalNumber: string;
  referenceNumber: string;
  /** ZOUR-65 F4: the GST registration this journal is filed under. NULL = primary. */
  locationId: string | null;
  method: string;
  currencySymbol: string;
  totalDebit: string;
  totalCredit: string;
  notes: string;
} => {
  if (!originalJson) {
    return {
      journalNumber: "N/A",
      referenceNumber: "N/A",
      locationId: null,
      method: "N/A",
      currencySymbol: "₹",
      totalDebit: "0.00",
      totalCredit: "0.00",
      notes: "N/A",
    };
  }

  const j = originalJson.journal || originalJson;
  const journalNumber =
    originalJson.journal_number ||
    originalJson.reference_number ||
    j.journal_number ||
    j.reference_number ||
    "N/A";
  const referenceNumber =
    originalJson.reference_number ||
    j.reference_number ||
    rawJson?.journal?.reference_number ||
    "N/A";
  // ZOUR-65 F4: read the location from wherever this approval's payload keeps
  // the journal. `?? null` and not `||`: NULL is a meaningful answer here (the
  // primary location), so an absent value must not fall through to the next
  // source and pick up a different journal's.
  const locationId =
    originalJson.location_id ??
    j.location_id ??
    rawJson?.journal?.location_id ??
    null;
  const typeVal = originalJson.type || j.type || rawJson?.journal?.type || "";
  const method =
    typeVal === "both"
      ? "Both"
      : typeVal === "cash"
        ? "Cash"
        : typeVal === "accrual"
          ? "Accrual"
          : typeVal
            ? typeVal.charAt(0).toUpperCase() + typeVal.slice(1)
            : "N/A";
  const currencySymbol =
    originalJson.currency_symbol || j.currency_symbol || "₹";
  const notes = originalJson.notes || j.notes || "N/A";

  const lineItems = originalJson.line_items || j.line_items || [];
  const { totalDebit, totalCredit } = calculateJournalTotals(lineItems);

  return {
    journalNumber,
    referenceNumber,
    locationId,
    method,
    currencySymbol,
    totalDebit: totalDebit.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    totalCredit: totalCredit.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    notes,
  };
};

/**
 * Get original and new journal debit/credit totals for update_after_push or month_close.
 * Used to show original vs new values instead of a single total.
 */
const getJournalOriginalAndNewTotals = (
  originalJson: any,
  rawJson: any,
): {
  currencySymbol: string;
  originalDebit: string;
  originalCredit: string;
  newDebit: string;
  newCredit: string;
  debitChanged: boolean;
  creditChanged: boolean;
} => {
  const origLineItems = originalJson?.line_items ?? [];
  const { totalDebit: origDebitNum, totalCredit: origCreditNum } =
    calculateJournalTotals(origLineItems);
  const newLineItems = rawJson?.line_items ?? [];
  const { totalDebit: newDebitNum, totalCredit: newCreditNum } =
    calculateJournalTotals(newLineItems);
  const currencySymbol =
    rawJson?.journal?.currency_symbol ?? originalJson?.currency_symbol ?? "₹";
  const fmt = (n: number) =>
    n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const debitChanged = Math.abs(origDebitNum - newDebitNum) >= 0.01;
  const creditChanged = Math.abs(origCreditNum - newCreditNum) >= 0.01;
  return {
    currencySymbol,
    originalDebit: fmt(origDebitNum),
    originalCredit: fmt(origCreditNum),
    newDebit: fmt(newDebitNum),
    newCredit: fmt(newCreditNum),
    debitChanged,
    creditChanged,
  };
};

// Helper: Convert dates from YYYY-MM-DD to DD-MM-YYYY
const convertDateFormat = (dateStr: string): string => {
  // Check if it's a date in YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (dateRegex.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }
  return dateStr;
};

// Helper: Format snake_case values to readable format
const formatValue = (val: string): string => {
  if (!val || val === "empty") return val;
  const strVal = String(val);

  // Check if it's a date and convert format
  const convertedDate = convertDateFormat(strVal);
  if (convertedDate !== strVal) {
    return convertedDate;
  }

  if (
    strVal.includes("_") &&
    !strVal.startsWith("[") &&
    !strVal.startsWith("{")
  ) {
    return strVal
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  return strVal;
};

const formatLineItemValue = (val: string, fieldLabel: string): string => {
  const isTaxName =
    (fieldLabel || "").toLowerCase().includes("tax name") ||
    (fieldLabel || "").toLowerCase().includes("tax_name");
  if (isTaxName) {
    if (!val || val === "empty" || String(val).trim() === "") return "No Tax";
    return String(val).toUpperCase();
  }
  return formatValue(val);
};

/**
 * Format field changes for Credit/Debit Notes
 */
const formatCreditDebitNoteChanges = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
): string[] => {
  const changes: string[] = [];
  const idFieldsToHide = [
    "customer_id",
    "vendor_id",
    "account_id",
    "tax_id",
    "invoice_id",
    "bill_id",
    "_id",
    "id",
  ];

  const lineItemChanges: Record<
    number,
    {
      quantity?: { old: string; new: string };
      rate?: { old: string; new: string };
    }
  > = {};

  fieldChanges.forEach((fc) => {
    const fieldLower = fc.field.toLowerCase();
    const formattedOld = formatValue(fc.old);
    const formattedNew = formatValue(fc.new);

    if (formattedOld === formattedNew) return;

    if (
      idFieldsToHide.some((idField) => fieldLower.includes(idField)) ||
      fieldLower.endsWith("_id") ||
      fieldLower.endsWith("id")
    ) {
      return;
    }

    const lineItemMatch = fc.field.match(
      /line_items\[(\d+)\]\.(quantity|rate)/i,
    );
    if (lineItemMatch) {
      const index = parseInt(lineItemMatch[1], 10);
      const field = lineItemMatch[2].toLowerCase() as "quantity" | "rate";

      if (!lineItemChanges[index]) {
        lineItemChanges[index] = {};
      }
      lineItemChanges[index][field] = {
        old: formattedOld,
        new: formattedNew,
      };
      return;
    }

    if (!fc.field.startsWith("line_items") && !fc.field.includes("_")) {
      changes.push(`${fc.field}: ${formattedOld} → ${formattedNew}`);
    }
  });

  Object.keys(lineItemChanges)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .forEach((indexStr) => {
      const index = parseInt(indexStr, 10);
      const itemChanges = lineItemChanges[index];
      const lineNumber = index + 1;

      if (itemChanges.quantity && itemChanges.rate) {
        changes.push(
          `Line Item ${lineNumber}: Quantity ${itemChanges.quantity.old} → ${itemChanges.quantity.new}, Rate ${itemChanges.rate.old} → ${itemChanges.rate.new}`,
        );
      } else if (itemChanges.quantity) {
        changes.push(
          `Line Item ${lineNumber}: Quantity ${itemChanges.quantity.old} → ${itemChanges.quantity.new}`,
        );
      } else if (itemChanges.rate) {
        changes.push(
          `Line Item ${lineNumber}: Rate ${itemChanges.rate.old} → ${itemChanges.rate.new}`,
        );
      }
    });

  return changes;
};

/**
 * Format credits applied information for display
 * Shows credit note number and invoice/bill number (not ID)
 * Uses reference_number from raw_json or original_credit_note.credit_note.json
 */
const formatCreditsApplied = (
  originalCreditNote: any,
  rawJson?: any,
): string[] => {
  const creditsInfo: string[] = [];

  if (!originalCreditNote?.credits_applied) {
    return creditsInfo;
  }

  const creditsApplied = originalCreditNote.credits_applied;
  const credits = creditsApplied.credits || [];

  if (credits.length === 0) {
    return creditsInfo;
  }

  // Get reference_number from original_credit_note.credit_note.json or raw_json
  let invoiceReferenceNumber: string | null = null;
  if (originalCreditNote?.credit_note?.json) {
    const creditNoteJson =
      typeof originalCreditNote.credit_note.json === "string"
        ? JSON.parse(originalCreditNote.credit_note.json)
        : originalCreditNote.credit_note.json;
    invoiceReferenceNumber = creditNoteJson.reference_number || null;
  } else if (rawJson?.reference_number) {
    invoiceReferenceNumber = rawJson.reference_number;
  }

  creditsInfo.push(`Credits Applied (${credits.length}):`);

  credits.forEach((credit: any, index: number) => {
    const creditNoteNumber =
      credit.creditnotes_number ||
      credit.vendor_credit_number ||
      `Credit Note ${index + 1}`;
    const amountApplied = credit.amount_applied || credit.amount || 0;
    const creditedDate = credit.credited_date || credit.date || "N/A";

    // Get invoice/bill ID
    const invoiceId = credit.creditnotes_invoice_id;
    const billId = credit.vendor_credit_bill_id;

    // Use reference_number from credit note JSON, or fallback to ID
    let documentInfo = "";
    if (invoiceId) {
      // For customer credits applied to invoices
      // Use reference_number from credit note JSON if available
      documentInfo = invoiceReferenceNumber
        ? `Invoice ${invoiceReferenceNumber}`
        : `Invoice ${invoiceId}`;
    } else if (billId) {
      // For vendor credits applied to bills
      // Use reference_number from credit note JSON if available
      documentInfo = invoiceReferenceNumber
        ? `Bill ${invoiceReferenceNumber}`
        : `Bill ${billId}`;
    } else {
      documentInfo = "Unknown Document";
    }

    creditsInfo.push(
      `  • ${creditNoteNumber}: ${amountApplied.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} applied to ${documentInfo} on ${creditedDate}`,
    );
  });

  return creditsInfo;
};

/**
 * Format field changes for Bank Transactions
 */
const formatBankTransactionChanges = (changeDetails: any): string[] => {
  const changes: string[] = [];
  if (!changeDetails?.raw_json) return changes;

  const rawJson = changeDetails.raw_json;
  const categoryType = rawJson.category_type || "";
  const categorizeData = rawJson.categorize_in_zoho_json;

  if (Array.isArray(rawJson.split_items)) {
    const currencySymbol = rawJson.currency_symbol || "₹";
    changes.push(
      `Original Transaction: ${rawJson.description || "N/A"}`,
      `Original Amount: ${currencySymbol} ${Number(
        rawJson.transaction_amount || 0,
      ).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      `Split Items: ${rawJson.split_items.length}`,
    );

    rawJson.split_items.forEach((item: any, index: number) => {
      changes.push(
        `  Item ${index + 1}: ${item.description || "N/A"} - ${currencySymbol} ${Number(
          item.amount || 0,
        ).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      );
    });

    return changes;
  }

  if (categoryType) {
    changes.push(`Category Type: ${formatValue(categoryType)}`);
  }

  // Handle customer_payment category
  if (categoryType === "customer_payment") {
    if (rawJson.customer_name) {
      changes.push(`Customer: ${rawJson.customer_name}`);
    }

    if (categorizeData) {
      const invoices = categorizeData.invoices || [];
      if (invoices.length > 0) {
        changes.push(`Invoices: ${invoices.length}`);
        invoices.forEach((invoice: any, index: number) => {
          const invoiceNumber =
            invoice.invoice_number ||
            invoice.invoice_id ||
            `Invoice ${index + 1}`;
          const total = invoice.total || 0;
          const balance = invoice.balance || 0;
          const amountApplied = invoice.amount_applied || 0;
          const currencySymbol =
            categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
          changes.push(
            `  ${invoiceNumber}: Total ${currencySymbol} ${Number(
              total,
            ).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}, Balance ${currencySymbol} ${Number(balance).toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}${
              amountApplied > 0
                ? `, Applied ${currencySymbol} ${Number(
                    amountApplied,
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : ""
            }`,
          );

          if (invoice.tds_tax_amount && invoice.tds_tax_amount > 0) {
            const tdsName = invoice.tds_tax_name || "TDS";
            const tdsPercentage = invoice.tds_tax_percentage
              ? ` (${invoice.tds_tax_percentage}%)`
              : "";
            changes.push(
              `    TDS: ${tdsName}${tdsPercentage} - ${currencySymbol} ${Number(
                invoice.tds_tax_amount,
              ).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
            );
          }
        });
      }

      if (rawJson.advance_payment && rawJson.advance_payment > 0) {
        const currencySymbol =
          categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
        changes.push(
          `Advance Payment: ${currencySymbol} ${Number(
            rawJson.advance_payment,
          ).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        );
      }
    }
  }

  // Handle vendor_payment category
  else if (categoryType === "vendor_payment") {
    if (rawJson.vendor_name) {
      changes.push(`Vendor: ${rawJson.vendor_name}`);
    }

    if (categorizeData) {
      const bills = categorizeData.bills || [];
      if (bills.length > 0) {
        changes.push(`Bills: ${bills.length}`);
        bills.forEach((bill: any, index: number) => {
          const billNumber =
            bill.bill_number || bill.bill_id || `Bill ${index + 1}`;
          const total = bill.total || 0;
          const balance = bill.balance || 0;
          const amountApplied = bill.amount_applied || 0;
          const currencySymbol =
            categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
          changes.push(
            `  ${billNumber}: Total ${currencySymbol} ${Number(
              total,
            ).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}, Balance ${currencySymbol} ${Number(balance).toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}${
              amountApplied > 0
                ? `, Applied ${currencySymbol} ${Number(
                    amountApplied,
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : ""
            }`,
          );

          if (bill.tds_tax_amount && bill.tds_tax_amount > 0) {
            const tdsName = bill.tds_tax_name || "TDS";
            const tdsPercentage = bill.tds_tax_percentage
              ? ` (${bill.tds_tax_percentage}%)`
              : "";
            changes.push(
              `    TDS: ${tdsName}${tdsPercentage} - ${currencySymbol} ${Number(
                bill.tds_tax_amount,
              ).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
            );
          }
        });
      }

      if (rawJson.advance_payment && rawJson.advance_payment > 0) {
        const currencySymbol =
          categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
        changes.push(
          `Advance Payment: ${currencySymbol} ${Number(
            rawJson.advance_payment,
          ).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        );
      }
    }
  }

  // Handle other category types
  else {
    if (rawJson.customer_name) {
      changes.push(`Customer: ${rawJson.customer_name}`);
    }
    if (rawJson.vendor_name) {
      changes.push(`Vendor: ${rawJson.vendor_name}`);
    }

    if (categorizeData) {
      const amount = categorizeData.amount || rawJson.amount || 0;
      const currencySymbol =
        categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
      if (amount > 0) {
        changes.push(
          `Amount: ${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        );
      }
    }
  }

  return changes;
};

/** Sort so "Post-close change" (month_close) is shown first. */
const sortActionTypesForDisplayLocal = sortActionTypesForDisplay;

/**
 * Format action types into readable text (Post-close change first).
 */
const formatActionTypes = (actionTypes?: string[]): string | null => {
  if (!actionTypes || actionTypes.length === 0) return null;

  const backend = getStoredAccountingBackend();
  const extraLabels: Record<string, string> = {
    edit: "Edit",
    approve: "Approve",
    split_transaction: "Split Transaction",
    split_transaction_amount: "Split Transaction Amount",
    advance: "Advance",
    unmatch: "Unmatch",
    create: "Create",
    expense_high_value: "Expense High Value",
    new: "New",
    revised: "Revised",
    auto_flagged: "Auto-flagged",
    create_recurring: "Create Recurring",
    cancel_recurring_before_end: "Delete Before End",
    reject: "Reject",
    duplicate: "Mark as Duplicate",
    write_off: "Write off",
    cancel_write_off: "Cancel write off",
    month_close: "Post-close change",
    ap_ar_create: "AP/AR Journal – Create",
    ap_ar_delete: "AP/AR Journal – Delete",
    bulk_upload: "Bulk Upload",
  };

  return formatCombinedActionTypes(
    actionTypes,
    backend,
    extraLabels,
    formatValue,
  );
};

/**
 * Format invoice/bill changes with GL override separation
 * Only used when override_gl_permanent is present
 */
const formatInvoiceBillChangesWithGLOverride = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
  changeDetails: any,
  entityType: string,
): { glOverrides: string[]; otherChanges: string[] } => {
  // Use a Map to track unique GL overrides by lineItemIndex to prevent duplicates
  const glOverrideMap = new Map<
    number,
    { lineItemIndex: number; old: string; new: string; reason?: string }
  >();
  const otherFieldChanges: Array<{
    field: string;
    old: string;
    new: string;
  }> = [];

  // Get all line item indices that have GL overrides from line_item_gl_data array
  const glOverrideLineItemIndices = new Set<number>();

  // Check if line_item_gl_data is an array (new format supporting multiple GL overrides)
  if (Array.isArray(changeDetails?.line_item_gl_data)) {
    changeDetails.line_item_gl_data.forEach((glData: any) => {
      if (glData.line_item_index !== undefined) {
        glOverrideLineItemIndices.add(glData.line_item_index);
      }
    });
  }
  // Also check for backward compatibility (single GL override object)
  else if (
    changeDetails?.line_item_gl_data &&
    changeDetails.line_item_gl_data.old_account_name &&
    changeDetails.line_item_gl_data.new_account_name
  ) {
    const lineItemIndex =
      changeDetails.line_item_index !== undefined
        ? changeDetails.line_item_index
        : 0;
    glOverrideLineItemIndices.add(lineItemIndex);
  }

  // Separate GL override changes from other changes
  fieldChanges.forEach((fc) => {
    // Check if this is a GL override change (Line Item X: Account Name)
    const glOverrideMatch = fc.field.match(/^Line Item (\d+): Account Name$/i);
    if (glOverrideMatch) {
      const lineItemNumber = parseInt(glOverrideMatch[1], 10);
      const lineItemIndex = lineItemNumber - 1;

      // Check if this line item has a GL override (from line_item_gl_data array)
      // If action_types includes override_gl_permanent or override_gl_once, treat all "Line Item X: Account Name" as GL overrides
      const hasGLOverrideAction =
        changeDetails?.action_types?.includes("override_gl_permanent") ||
        changeDetails?.action_types?.includes("override_gl_once");

      const isGLOverride =
        hasGLOverrideAction &&
        (glOverrideLineItemIndices.has(lineItemIndex) ||
          // If no line_item_gl_data but action_types has GL override, treat all as GL overrides
          (glOverrideLineItemIndices.size === 0 && hasGLOverrideAction));

      if (isGLOverride) {
        // Only add if we haven't seen this line item index before
        if (!glOverrideMap.has(lineItemIndex)) {
          glOverrideMap.set(lineItemIndex, {
            lineItemIndex,
            old: fc.old,
            new: fc.new,
          });
        }
      } else {
        // This is a regular account change (scope "once"), not a GL override
        otherFieldChanges.push(fc);
      }
    } else {
      otherFieldChanges.push(fc);
    }
  });

  // Also add GL overrides from line_item_gl_data array if not already in field_changes
  if (Array.isArray(changeDetails?.line_item_gl_data)) {
    changeDetails.line_item_gl_data.forEach((glData: any) => {
      if (
        glData.line_item_index !== undefined &&
        glData.old_account_name &&
        glData.new_account_name
      ) {
        // Only add if we haven't seen this line item index before
        if (!glOverrideMap.has(glData.line_item_index)) {
          glOverrideMap.set(glData.line_item_index, {
            lineItemIndex: glData.line_item_index,
            old: glData.old_account_name,
            new: glData.new_account_name,
            reason: glData.reason?.trim(),
          });
        } else if (glData.reason?.trim()) {
          const existing = glOverrideMap.get(glData.line_item_index);
          if (existing) {
            glOverrideMap.set(glData.line_item_index, {
              ...existing,
              reason: glData.reason.trim(),
            });
          }
        }
      }
    });
  } else if (
    changeDetails?.reason?.trim() &&
    changeDetails?.line_item_index !== undefined &&
    glOverrideMap.has(changeDetails.line_item_index)
  ) {
    const existing = glOverrideMap.get(changeDetails.line_item_index);
    if (existing) {
      glOverrideMap.set(changeDetails.line_item_index, {
        ...existing,
        reason: changeDetails.reason.trim(),
      });
    }
  }

  // Convert Map to array
  const glOverrideChanges = Array.from(glOverrideMap.values());

  // Format GL overrides
  const glOverrides: string[] = [];
  if (glOverrideChanges.length > 0) {
    glOverrideChanges
      .sort((a, b) => a.lineItemIndex - b.lineItemIndex)
      .forEach((gl) => {
        const formattedOld = formatValue(gl.old);
        const formattedNew = formatValue(gl.new);
        const lineItemNumber = gl.lineItemIndex + 1;
        glOverrides.push(`GL Override request for Line Item ${lineItemNumber}`);
        glOverrides.push(`Account: ${formattedOld} → ${formattedNew}`);
        if (gl.reason) {
          glOverrides.push(`Reason: ${gl.reason}`);
        }
      });
  }

  const otherChanges: string[] = [];
  if (otherFieldChanges.length > 0) {
    const kind = entityType === "invoice" ? "invoice" : "bill";
    otherChanges.push(
      ...formatInvoiceBillFieldChangesForApprovalList(kind, otherFieldChanges),
    );
  }

  return { glOverrides, otherChanges };
};

/**
 * Format journal line items from raw_json for create_recurring actions
 * Shows Account, Contact (if exists), and Debit/Credit for each line item
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatJournalLineItemsFromRawJson = (rawJson?: any): string[] => {
  if (!rawJson || !rawJson.line_items || !Array.isArray(rawJson.line_items)) {
    return [];
  }

  const changes: string[] = [];
  const currencySymbol = rawJson.currency_symbol || "₹";

  rawJson.line_items.forEach((item: any, index: number) => {
    const lineNumber = index + 1;
    const accountName = item.account_name || "N/A";
    const contactName = item.contact_name || null;
    const amount = parseFloat(item.amount || 0);
    const isDebitOrCredit = item.is_debit_or_credit?.toLowerCase();

    changes.push(`Line Item ${lineNumber}:`);
    changes.push(`  Account: ${accountName}`);

    if (contactName) {
      changes.push(`  Contact: ${contactName}`);
    }

    if (item.reference_display) {
      changes.push(`  Invoice / Bill: ${item.reference_display}`);
    }

    if (isDebitOrCredit === "debit" && amount > 0) {
      changes.push(
        `  Debit: ${currencySymbol} ${amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      );
    } else if (isDebitOrCredit === "credit" && amount > 0) {
      changes.push(
        `  Credit: ${currencySymbol} ${amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      );
    }

    const rate = parseFloat(item.exchange_rate ?? 0);
    if (rate > 0 && amount > 0) {
      const contactSymbol = item.contact_currency_symbol || currencySymbol;
      const amountInContactCurrency = amount / rate;
      const fmt = (n: number) =>
        n.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      changes.push(`  Exchange Rate: ${fmt(rate)}`);
      changes.push(
        `  Amount (${contactSymbol}): ${fmt(amountInContactCurrency)}`,
      );
      changes.push(`  Amount in INR (₹): ${fmt(amount)}`);
    }

    changes.push("");
  });

  return changes;
};

/**
 * Format journal changes for update_after_push action
 * Only shows non-empty changes where old !== new
 */
const formatJournalChangesForUpdateAfterPush = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
  rawJson?: any,
  originalJson?: any,
): string[] => {
  // Helper to normalize empty values
  const normalizeValue = (val: string): string => {
    if (
      !val ||
      val === "null" ||
      val === "undefined" ||
      val === "(empty)" ||
      val === "empty"
    ) {
      return "";
    }
    return val.trim();
  };

  // Filter out empty changes and only keep actual changes
  const filteredChanges = fieldChanges.filter((fc) => {
    const normalizedOld = normalizeValue(fc.old);
    const normalizedNew = normalizeValue(fc.new);
    // Only include if both old and new are not empty AND they are different
    return (
      normalizedOld !== "" &&
      normalizedNew !== "" &&
      normalizedOld !== normalizedNew
    );
  });

  // If no valid changes, return empty array
  if (filteredChanges.length === 0) {
    return [];
  }

  // Use the standard formatter but with filtered changes
  return formatJournalChangesForBanner(filteredChanges, rawJson, originalJson);
};

/**
 * Format changes for display in detail panel
 */
const formatChangesForDetailPanel = (
  changeDetails: any,
  entityType: string,
  actionTypes?: string[],
  taxes?: TaxEntity[],
): { glOverrides: string[]; otherChanges: string[] } => {
  // For credit_note/debit_note DELETE action: show original document data (same as ReviewRequestModal)
  // so reviewer/manager sees what is in the doc before approving delete
  const isDeleteCreditDebit =
    (entityType === "credit_note" || entityType === "debit_note") &&
    actionTypes?.includes("delete_after_push_to_zoho");
  if (isDeleteCreditDebit && changeDetails) {
    const rawJson = changeDetails.raw_json || changeDetails.original_json;
    let docJson = rawJson;
    if (
      !docJson &&
      entityType === "credit_note" &&
      changeDetails.original_credit_note?.credit_note?.json
    ) {
      const j = changeDetails.original_credit_note.credit_note.json;
      docJson = typeof j === "string" ? JSON.parse(j) : j;
    }
    if (docJson) {
      const headerSection = formatCreditDebitNoteHeaderFromRawJson(docJson);
      const lineItemsSection = formatAllLineItemsFromRawJson(
        docJson,
        entityType,
      );
      const otherChanges = [
        ...(headerSection.length > 0 ? ["", ...headerSection] : []),
        ...lineItemsSection,
      ];
      return { glOverrides: [], otherChanges };
    }
  }

  // For invoice/bill delete_after_push_to_zoho: show full document details (same as create) so reviewer sees what will be deleted
  const isDeleteInvoiceOrBill =
    (entityType === "invoice" || entityType === "bill") &&
    actionTypes?.includes("delete_after_push_to_zoho");
  if (isDeleteInvoiceOrBill && changeDetails?.raw_json) {
    const deleteSummary = formatInvoiceBillCreateSummaryFromRawJson(
      changeDetails.raw_json,
      entityType,
    );
    if (deleteSummary.length > 0) {
      return { glOverrides: [], otherChanges: deleteSummary };
    }
  }

  // For invoice write_off / cancel_write_off: show invoice summary (number, total, balance, paid)
  const isInvoiceWriteOff =
    entityType === "invoice" &&
    (actionTypes?.includes("write_off") ||
      actionTypes?.includes("cancel_write_off"));
  if (isInvoiceWriteOff && changeDetails?.raw_json) {
    const r = changeDetails.raw_json;
    const paid =
      r.paid != null
        ? String(r.paid)
        : r.total != null && r.balance != null
          ? String(r.total - r.balance)
          : "—";
    const otherChanges = [
      "Invoice:",
      `  Number: ${r.invoice_number ?? "—"}`,
      `  Total: ${r.total ?? "—"}`,
      `  Balance: ${r.balance ?? "—"}`,
      `  Paid: ${paid}`,
    ];
    return { glOverrides: [], otherChanges };
  }

  // Journal draft flow: from saved-as-draft (push_to_zoho, push_to_zoho_without_attachment, create_recurring, ap_ar_create, ap_ar_delete). Show full journal from raw_json in details block; no "Changes Requested" list.
  const isJournalDraftFlowForFormat =
    (entityType === "journal" || entityType === "ap_ar_journal") &&
    (actionTypes?.includes("push_to_zoho") ||
      actionTypes?.includes("push_to_zoho_without_attachment") ||
      actionTypes?.includes("create_recurring") ||
      actionTypes?.includes("ap_ar_create") ||
      actionTypes?.includes("ap_ar_delete")) &&
    !actionTypes?.includes("update_after_push");
  if (isJournalDraftFlowForFormat) {
    return { glOverrides: [], otherChanges: [] };
  }

  // For journal with update_after_push action, filter out empty changes
  if (entityType === "journal" && actionTypes?.includes("update_after_push")) {
    if (
      !changeDetails?.field_changes ||
      changeDetails.field_changes.length === 0
    ) {
      return { glOverrides: [], otherChanges: [] };
    }
    return {
      glOverrides: [],
      otherChanges: formatJournalChangesForUpdateAfterPush(
        changeDetails.field_changes,
        changeDetails.raw_json,
        changeDetails.original_json,
      ),
    };
  }

  // Customer / vendor delete review: show concise summary only (no Zoho IDs, no empty sections)
  const isContactDelete =
    (entityType === "customer" || entityType === "vendor") &&
    Boolean(actionTypes?.includes("delete"));
  if (isContactDelete && changeDetails?.raw_json) {
    const r = changeDetails.raw_json;
    const label = entityType === "customer" ? "Customer" : "Vendor";
    const name = r.customer_name ?? r.vendor_legal_name ?? r.name ?? "—";
    return {
      glOverrides: [],
      otherChanges: [
        `Requested action: Delete ${label}`,
        `Name: ${name}`,
        `This ${label.toLowerCase()} will be permanently removed from Zoho Books.`,
      ],
    };
  }

  const isMastersBulkUpload =
    (entityType === "customer" ||
      entityType === "vendor" ||
      entityType === "gl") &&
    Boolean(actionTypes?.includes("bulk_upload"));
  if (isMastersBulkUpload && changeDetails?.raw_json) {
    const raw = changeDetails.raw_json as { rows?: unknown[] };
    const n = Array.isArray(raw.rows) ? raw.rows.length : 0;
    const label =
      entityType === "customer"
        ? "customers"
        : entityType === "vendor"
          ? "vendors"
          : "GL accounts";
    return {
      glOverrides: [],
      otherChanges: [
        `Requested action: Bulk Upload (${n} ${label})`,
        entityType === "gl"
          ? "After approval, GL accounts will be created and saved here."
          : `After approval, ${label.toLowerCase()} will be created.`,
      ],
    };
  }

  // For customer/vendor, we use raw_json AND field_changes
  if (entityType === "customer" || entityType === "vendor") {
    let allChanges: string[] = [];
    if (entityType === "customer") {
      allChanges = formatCustomerDetails(
        changeDetails.raw_json,
        changeDetails.field_changes,
      );
    } else {
      allChanges = formatVendorDetails(
        changeDetails.raw_json,
        changeDetails.field_changes,
      );
    }
    return { glOverrides: [], otherChanges: allChanges };
  }

  // For invoice/bill/credit_note/debit_note with no field_changes, still show document header + line items from raw_json
  const hasLineItemsEntities =
    entityType === "invoice" ||
    entityType === "bill" ||
    entityType === "credit_note" ||
    entityType === "debit_note";
  const isCreditOrDebit =
    entityType === "credit_note" || entityType === "debit_note";
  if (
    hasLineItemsEntities &&
    (!changeDetails?.field_changes ||
      changeDetails.field_changes.length === 0) &&
    changeDetails?.raw_json
  ) {
    // Create action for manual invoice/bill: show structured summary
    const isCreateInvoiceOrBill =
      (entityType === "invoice" || entityType === "bill") &&
      actionTypes?.includes("create");
    if (isCreateInvoiceOrBill) {
      const createSummary = formatInvoiceBillCreateSummaryFromRawJson(
        changeDetails.raw_json,
        entityType,
      );
      if (createSummary.length > 0) {
        return { glOverrides: [], otherChanges: createSummary };
      }
    }
    if (changeDetails.raw_json?.line_items?.length > 0) {
      const headerSection = isCreditOrDebit
        ? formatCreditDebitNoteHeaderFromRawJson(changeDetails.raw_json)
        : [];
      const lineItemsSection = formatAllLineItemsFromRawJson(
        changeDetails.raw_json,
        entityType,
      );
      const otherChanges = [
        ...(headerSection.length > 0 ? ["", ...headerSection] : []),
        ...lineItemsSection,
      ];
      return { glOverrides: [], otherChanges };
    }
  }

  const noFieldChanges =
    !changeDetails?.field_changes || changeDetails.field_changes.length === 0;
  const invoiceBillHasJsonPair =
    (entityType === "invoice" || entityType === "bill") &&
    changeDetails?.original_json != null &&
    changeDetails?.raw_json != null;
  if (noFieldChanges && !invoiceBillHasJsonPair) {
    return { glOverrides: [], otherChanges: [] };
  }

  // Check if override_gl_permanent is present
  const hasGLOverride = actionTypes?.includes("override_gl_permanent");

  // For invoices/bills with GL override, separate GL overrides from other changes
  if ((entityType === "invoice" || entityType === "bill") && hasGLOverride) {
    const glResult = formatInvoiceBillChangesWithGLOverride(
      changeDetails.field_changes,
      changeDetails,
      entityType,
    );
    const hasFieldChanges = (changeDetails.field_changes?.length ?? 0) > 0;
    const lineItemsSection = hasFieldChanges
      ? []
      : formatAllLineItemsFromRawJson(changeDetails.raw_json, entityType);
    return {
      glOverrides: glResult.glOverrides,
      otherChanges: [...glResult.otherChanges, ...lineItemsSection],
    };
  }

  // For other cases, format normally
  let allChanges: string[] = [];
  switch (entityType) {
    case "invoice":
    case "bill":
      break;
    case "credit_note":
    case "debit_note":
      allChanges = formatCreditDebitNoteChanges(changeDetails.field_changes);
      break;
    case "journal":
    case "ap_ar_journal":
      allChanges = formatJournalChangesForBanner(
        changeDetails.field_changes,
        changeDetails.raw_json,
        changeDetails.original_json,
      );
      break;
    case "bank_transaction":
      allChanges = formatBankTransactionChanges(changeDetails);
      break;
    default:
      allChanges = [];
  }

  const isInvoiceOrBill = entityType === "invoice" || entityType === "bill";
  if (isInvoiceOrBill) {
    const kind = entityType === "invoice" ? "invoice" : "bill";
    allChanges = formatInvoiceBillReviewChangesForApprovalDisplay(
      kind,
      changeDetails,
      taxes,
      actionTypes?.[0],
    );
  }

  const hasFieldChanges = (changeDetails.field_changes?.length ?? 0) > 0;
  const hasInvoiceBillSnapshotPair =
    isInvoiceOrBill &&
    changeDetails?.original_json != null &&
    changeDetails?.raw_json != null;
  if (hasLineItemsEntities && changeDetails?.raw_json) {
    // For credit_note/debit_note prepend document header; append full line items when no original_json
    const headerSection = isCreditOrDebit
      ? formatCreditDebitNoteHeaderFromRawJson(changeDetails.raw_json)
      : [];
    const skipFullLineItems =
      isInvoiceOrBill && (hasFieldChanges || hasInvoiceBillSnapshotPair);
    const lineItemsSection = skipFullLineItems
      ? []
      : formatAllLineItemsFromRawJson(changeDetails.raw_json, entityType);
    allChanges = [
      ...(headerSection.length > 0 ? ["", ...headerSection] : []),
      ...allChanges,
      ...lineItemsSection,
    ];
  }

  return { glOverrides: [], otherChanges: allChanges };
};

export const ApprovalDetailViewPanel: React.FC<
  ApprovalDetailViewPanelProps
> = ({ approval, onClose }) => {
  const { taxes } = useTaxes();
  const navigate = useNavigate();
  // ZOUR-65 F4: names for the GST registration a journal is filed under.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();

  /** "Bengaluru Branch · 29AAECN3779L1ZQ (KA)". NULL resolves to the primary. */
  const describeLocation = (locationId: string | null): string => {
    const match =
      activeLocations.find(
        (candidate) => candidate.zoho_location_id === locationId,
      ) ?? (locationId ? null : primaryLocation);

    if (!match) return "\u2014";

    return `${match.name}${match.gstin ? ` \u00b7 ${match.gstin}` : ""}${
      match.state_code ? ` (${match.state_code})` : ""
    }`;
  };
  const [documentSignedUrl, setDocumentSignedUrl] = useState<string | null>(
    null,
  );
  const [documentFileName, setDocumentFileName] = useState<string | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [complianceProofs, setComplianceProofs] = useState<
    ComplianceProofEntity[]
  >([]);
  const [complianceProofsLoading, setComplianceProofsLoading] =
    useState<boolean>(false);
  const [bulkImportRowsModalOpen, setBulkImportRowsModalOpen] = useState(false);

  const rawJson = approval?.changeDetails?.raw_json;
  const entityType = approval?.entityType;
  // Use entity_id from approval, or try to get from raw_json
  const entityId = approval?.entityId || rawJson?.id;
  // For invoice/bill update_after_push or edit, show the post-validation
  // baseline in Details so system tax/TDS changes align with the line items.
  const actionTypesList = approval?.changeDetails?.action_types || [];
  const hasOriginalJson =
    approval?.changeDetails?.original_json &&
    Object.keys(approval.changeDetails.original_json).length > 0;
  const isShowOriginalDocument =
    (entityType === "invoice" || entityType === "bill") &&
    hasOriginalJson &&
    (actionTypesList.includes("update_after_push") ||
      actionTypesList.includes("edit"));
  const displayJson =
    isShowOriginalDocument && approval?.changeDetails?.original_json
      ? buildInvoiceBillBeforeRequestedEditsJson(
          approval.changeDetails,
          approval.changeDetails.original_json,
        )
      : rawJson;

  const displayJsonRecord =
    displayJson &&
    typeof displayJson === "object" &&
    !Array.isArray(displayJson)
      ? (displayJson as Record<string, unknown>)
      : null;

  const { resolvedGstTreatment, resolvedGstCategory } = useResolvedVendorGst(
    entityType === "bill" ? displayJsonRecord : null,
    entityType === "bill",
  );

  // Fetch document details for invoices/bills
  const {
    invoiceDocument: invoiceDoc,
    invoiceJson: invoiceJsonData,
    signedUrl: invoiceSignedUrl,
    fileName: invoiceFileName,
    loading: invoiceLoading,
  } = useInvoiceDetails(
    entityType === "invoice" && entityId ? entityId.toString() : null,
  );

  const {
    billDocument: billDoc,
    billJson: billJsonData,
    signedUrl: billSignedUrl,
    fileName: billFileName,
    loading: billLoading,
  } = useBillDetails(
    entityType === "bill" && entityId ? entityId.toString() : null,
  );

  // Fetch document signed URL for invoices and bills using getSignedUrlForDocument
  useEffect(() => {
    const fetchDocumentSignedUrl = async () => {
      if ((entityType === "invoice" || entityType === "bill") && entityId) {
        try {
          setDocumentLoading(true);
          const response = await getSignedUrlForDocument({
            documentId: entityId.toString(),
            documentType: entityType,
            expiration: 3600,
          });
          if (response.success && response.data) {
            setDocumentSignedUrl(response.data.signedUrl);
            setDocumentFileName(response.data.filename || null);
          }
        } catch (error) {
          console.error("Error fetching document signed URL:", error);
        } finally {
          setDocumentLoading(false);
        }
      }
    };

    fetchDocumentSignedUrl();
  }, [entityType, entityId]);

  // Fetch compliance proofs for tenant_compliance_calendar
  useEffect(() => {
    const fetchComplianceProofs = async () => {
      if (entityType === "tenant_compliance_calendar") {
        // Get compliance_id from raw_json or entityId
        const complianceId = rawJson?.compliance_id || entityId;
        if (!complianceId) {
          return;
        }

        try {
          setComplianceProofsLoading(true);
          const response = await getCompliance(complianceId);
          if (response.success && response.data) {
            setComplianceProofs(response.data.proofs || []);
          }
        } catch (error) {
          console.error("Error fetching compliance proofs:", error);
          setComplianceProofs([]);
        } finally {
          setComplianceProofsLoading(false);
        }
      } else {
        setComplianceProofs([]);
      }
    };

    fetchComplianceProofs();
  }, [entityType, entityId, rawJson]);

  // Use dedicated API-fetched signed URL if available, otherwise fall back to hooks
  const finalSignedUrl =
    documentSignedUrl ||
    (entityType === "invoice" ? invoiceSignedUrl : billSignedUrl);
  const finalFileName =
    documentFileName ||
    (entityType === "invoice" ? invoiceFileName : billFileName);
  const finalDocumentLoading =
    documentLoading ||
    (entityType === "invoice" ? invoiceLoading : billLoading);

  const validationSnapshotLineItems = getValidationSnapshotLineItems(
    approval?.changeDetails,
  );

  // Use invoiceJson/billJson from hooks; for update_after_push/edit prefer the
  // validation snapshot so Details means "before requested edits".
  // For credit notes, use original_credit_note.credit_note.json.line_items
  // For debit notes, use raw_json from change_details
  const lineItems =
    entityType === "invoice"
      ? isShowOriginalDocument &&
        validationSnapshotLineItems?.length
        ? validationSnapshotLineItems
        : isShowOriginalDocument &&
        approval?.changeDetails?.original_json?.line_items?.length
        ? approval.changeDetails.original_json.line_items
        : invoiceJsonData?.line_items || []
      : entityType === "bill"
        ? isShowOriginalDocument &&
          validationSnapshotLineItems?.length
          ? validationSnapshotLineItems
          : isShowOriginalDocument &&
          approval?.changeDetails?.original_json?.line_items?.length
          ? approval.changeDetails.original_json.line_items
          : billJsonData?.line_items || []
        : entityType === "credit_note"
          ? (() => {
              // Use original_credit_note line items for credit notes
              if (
                approval?.changeDetails?.original_credit_note?.credit_note?.json
              ) {
                const creditNoteJson =
                  typeof approval.changeDetails.original_credit_note.credit_note
                    .json === "string"
                    ? JSON.parse(
                        approval.changeDetails.original_credit_note.credit_note
                          .json,
                      )
                    : approval.changeDetails.original_credit_note.credit_note
                        .json;
                return creditNoteJson.line_items || [];
              }
              return rawJson?.line_items || [];
            })()
          : entityType === "debit_note"
            ? rawJson?.line_items || []
            : [];

  // Get currency symbol from the JSON data (use displayJson for invoice/bill when showing original)
  const currencySymbol =
    entityType === "invoice"
      ? (isShowOriginalDocument && displayJson?.currency_symbol) ||
        invoiceJsonData?.currency_symbol ||
        "INR"
      : entityType === "bill"
        ? (isShowOriginalDocument && displayJson?.currency_symbol) ||
          billJsonData?.currency_symbol ||
          "INR"
        : entityType === "credit_note" || entityType === "debit_note"
          ? rawJson?.currency_symbol || "INR"
          : "INR";

  useEffect(() => {
    if (!approval) {
      // View panel - no comment state to reset
    }
  }, [approval]);

  if (!approval) {
    return null;
  }

  /**
   * Helper function to handle invoice approval actions
   * Handles reject, duplicate, push_to_zoho, update_after_push, override_gl_permanent, and edit actions
   */

  /**
   * Helper function to handle bill approval actions
   * Handles reject, duplicate, push_to_zoho, update_after_push, override_gl_permanent, and edit actions
   */

  /**
   * Helper function to handle bank transaction approval actions
   * Calls the appropriate categorize API based on category_type
   * OR uncategorizes if it's an unmatch action
   */

  // Format all field_changes for display (only GL overrides shown in view-only mode)
  const actionTypes = approval?.changeDetails?.action_types || [];
  const formattedChangesResult = approval?.changeDetails
    ? formatChangesForDetailPanel(
        approval.changeDetails,
        entityType || "",
        actionTypes,
        taxes,
      )
    : { glOverrides: [], otherChanges: [] };

  const glOverrides = formattedChangesResult.glOverrides;
  const otherChanges = formattedChangesResult.otherChanges;
  const isBulkUploadDetail =
    (approval?.entityType === "customer" ||
      approval?.entityType === "vendor" ||
      approval?.entityType === "gl") &&
    (actionTypes.includes("bulk_upload") ||
      (approval?.actionCode ?? "").toLowerCase() === "bulk_upload");
  const bulkImportReviewRows = (() => {
    const r = approval?.changeDetails?.raw_json as
      | {
          rows?: Array<{
            excel_row: number;
            payload: Record<string, unknown>;
          }>;
        }
      | undefined;
    if (!r?.rows || !Array.isArray(r.rows)) return [];
    return r.rows;
  })();
  const hasGLOverride = actionTypes.includes("override_gl_permanent");
  const isJournalLikeApproval =
    approval.entityType === "journal" ||
    approval.entityType === "ap_ar_journal";
  const hasCreateRecurringOnly =
    isJournalLikeApproval &&
    actionTypes.includes("create_recurring") &&
    !actionTypes.includes("push_to_zoho") &&
    !actionTypes.includes("update_after_push");
  const isJournalDraftFlow =
    isJournalLikeApproval &&
    (actionTypes.includes("push_to_zoho") ||
      actionTypes.includes("push_to_zoho_without_attachment") ||
      actionTypes.includes("create_recurring") ||
      actionTypes.includes("ap_ar_create") ||
      actionTypes.includes("ap_ar_delete")) &&
    !actionTypes.includes("update_after_push");
  const isCreateInvoiceOrBill =
    (approval.entityType === "invoice" || approval.entityType === "bill") &&
    actionTypes.includes("create");
  const isInvoiceWriteOff =
    approval.entityType === "invoice" &&
    (actionTypes.includes("write_off") ||
      actionTypes.includes("cancel_write_off"));

  return (
    <div className="flex w-[540px] flex-none flex-col items-start self-stretch overflow-hidden border-l border-solid border-neutral-border bg-default-background">
      <div className="flex w-full flex-col items-start gap-6 px-6 py-6 overflow-auto">
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
              <Badge variant="neutral">
                {getApprovalEntityDisplayName(approval.entityType, "badge")}
              </Badge>
              {approval.entityType === "bank_transaction" ? (
                <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                  Bank Transaction - {approval.actionName}
                </span>
              ) : approval.entityType === "bank_account" ? (
                <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                  {getBankAccountApprovalTitle(approval.actionCode, rawJson)}
                  {rawJson?.account_name
                    ? ` – ${String(rawJson.account_name)}`
                    : ""}
                </span>
              ) : approval.entityType === "month_end_checklist_item" ? (
                <div className="flex min-w-0 flex-col items-start gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {approval.itemTitle || "Checklist Item"}
                  </span>
                  {approval.itemDescription ? (
                    <span className="text-body font-body text-subtext-color">
                      {approval.itemDescription}
                    </span>
                  ) : null}
                </div>
              ) : (
                <span className="text-body-bold font-body-bold text-default-font">
                  {(() => {
                    // Get entity name
                    const entityName = getApprovalEntityDisplayName(
                      approval.entityType,
                      "header",
                    );

                    // Get reference number
                    let refNumber: string | null = null;
                    if (approval.entityType === "tenant_compliance_calendar") {
                      // For compliance, use compliance_form_name from raw_json
                      refNumber = rawJson?.compliance_form_name || null;
                    } else if (isJournalLikeApproval) {
                      if (approval.changeDetails?.original_json) {
                        refNumber = formatJournalInfo(
                          approval.changeDetails.original_json,
                        ).journalNumber;
                      } else {
                        refNumber = getReferenceNumber(
                          rawJson,
                          approval.entityType,
                        );
                      }
                    } else if (approval.entityType === "bank_transaction") {
                      // Don't show reference number for bank transactions
                      refNumber = null;
                    } else if (approval.entityType === "bank_account") {
                      refNumber =
                        (rawJson?.account_name as string) || "New bank account";
                    } else if (approval.entityType === "gl") {
                      refNumber = (rawJson?.account_name as string) || null;
                    } else {
                      refNumber = getReferenceNumber(
                        rawJson,
                        approval.entityType,
                      );
                    }

                    // If reference number is N/A, use action name as fallback (formatted; no snake_case in UI)
                    if (refNumber === "N/A" && approval.actionName) {
                      const actionNameLabels: Record<string, string> = {
                        write_off: "Write off",
                        cancel_write_off: "Cancel write off",
                      };
                      refNumber =
                        actionNameLabels[approval.actionName] ??
                        approval.actionName
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ");
                    }

                    // Format action types
                    const actionTypes =
                      approval.changeDetails?.action_types || [];
                    const backend = getStoredAccountingBackend();
                    const actionLabels: Record<string, string> = {
                      ...getReviewActionLabels(backend),
                      edit: "Edit",
                      approve: "Approve",
                      split_transaction: "Split Transaction",
                      advance: "Advance",
                      unmatch: "Unmatch",
                      create: "Create",
                      expense_high_value: "Expense High Value",
                      new: "New",
                      revised: "Revised",
                      auto_flagged: "Auto-flagged",
                      create_recurring: "Create Recurring",
                      cancel_recurring_before_end: "Delete Before End",
                      reject: "Reject",
                      duplicate: "Mark as Duplicate",
                      write_off: "Write off",
                      cancel_write_off: "Cancel write off",
                      month_close: "Post-close change",
                      ap_ar_create: "AP/AR Journal – Create",
                      ap_ar_delete: "AP/AR Journal – Delete",
                      bulk_upload: "Bulk Upload",
                    };

                    const glActionLabelOverride: Record<string, string> = {
                      create: "Create",
                      edit: "Update",
                      update: "Update",
                      delete: "Delete",
                    };

                    // Sort so Post-close change first, then filter
                    const sortedActionTypes =
                      sortActionTypesForDisplayLocal(actionTypes);
                    const filteredActionTypes = sortedActionTypes.filter(
                      (action) =>
                        !(
                          action === "edit" &&
                          actionTypes.includes("push_to_zoho")
                        ),
                    );

                    const formattedActions =
                      filteredActionTypes.length > 0
                        ? filteredActionTypes
                            .map((action) =>
                              approval.entityType === "gl"
                                ? (glActionLabelOverride[action] ??
                                  actionLabels[action] ??
                                  formatValue(action))
                                : actionLabels[action] || formatValue(action),
                            )
                            .join(" + ")
                        : null;

                    // Build the header string: Entity Type - Reference Number (Action Types)
                    let header = entityName;
                    if (refNumber && refNumber !== "N/A") {
                      header += ` - ${refNumber}`;
                    }
                    // Omit parenthetical when it would duplicate refNumber, or for AP/AR Journal – Delete per product requirement
                    if (
                      formattedActions &&
                      formattedActions !== refNumber &&
                      formattedActions !== "AP/AR Journal – Delete"
                    ) {
                      header += ` (${formattedActions})`;
                    }

                    return header;
                  })()}
                </span>
              )}
            </div>
            <IconButton
              variant="neutral-secondary"
              size="medium"
              icon={<FeatherX />}
              onClick={onClose}
              title="Close"
              aria-label="Close"
              className="flex-none shrink-0"
            />
          </div>
          {/* Closed month indicator */}
          {(approval?.changeDetails?.closed_month === true ||
            actionTypes.includes("month_close")) && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-100 px-4 py-3 border border-solid border-neutral-200">
              <span className="text-body-bold font-body-bold text-neutral-700">
                Closed month
              </span>
              <span className="text-body font-body text-neutral-700">
                This request is for a change in a closed month and requires
                review and approval.
              </span>
            </div>
          )}
          {/* Action Types Display */}
          {actionTypes.length > 0 && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-info-50 px-4 py-3 border border-solid border-info-200">
              <span className="text-body-bold font-body-bold text-info-700">
                Review Type
              </span>
              <span className="text-body font-body text-info-700">
                {formatActionTypes(actionTypes)}
              </span>
            </div>
          )}
          {/* GL Overrides Display (if override_gl_permanent is present) */}
          {hasGLOverride && glOverrides.length > 0 && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-brand-50 px-4 py-3 border border-solid border-brand-200">
              <div className="flex items-center gap-2">
                <FeatherAlertCircle className="text-body font-body text-brand-700" />
                <span className="text-body-bold font-body-bold text-brand-800">
                  GL Override Requests
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-1">
                {glOverrides.map((change, index) => (
                  <span
                    key={index}
                    className="text-body font-body text-brand-800 whitespace-pre-line"
                  >
                    {change}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Removed "Changes Requested" section for view-only mode */}
        </div>
        {/* Accountant Notes */}
        {approval.requestComments && (
          <>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Accountant Notes
              </span>
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                <span className="text-body font-body text-default-font whitespace-pre-line">
                  {approval.requestComments}
                </span>
              </div>
            </div>
          </>
        )}
        {/* Raised by - after Accountant Notes, across all entity types */}
        {!approval.requestComments && (
          <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        )}
        <div className="flex w-full flex-col items-start gap-3">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Raised by
            </span>
            <span className="text-body font-body text-default-font">
              {approval.requesterName || "Unknown"}
            </span>
          </div>
        </div>
        {/* Changes Requested / Other Changes - for invoice and bill only (immediately after Raised by) */}
        {(approval.entityType === "invoice" ||
          approval.entityType === "bill") &&
          otherChanges.length > 0 &&
          !isInvoiceWriteOff && (
            <>
              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
              <div
                className={
                  actionTypes.includes("delete_after_push_to_zoho")
                    ? "flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200"
                    : "flex w-full flex-col items-start gap-2 rounded-md bg-warning-50 px-4 py-3"
                }
              >
                <div className="flex items-center gap-2">
                  {!actionTypes.includes("delete_after_push_to_zoho") && (
                    <FeatherAlertCircle className="text-body font-body text-warning-700" />
                  )}
                  <span
                    className={
                      actionTypes.includes("delete_after_push_to_zoho")
                        ? "text-body-bold font-body-bold text-default-font"
                        : "text-body-bold font-body-bold text-warning-800"
                    }
                  >
                    {actionTypes.includes("delete_after_push_to_zoho")
                      ? "Document data"
                      : hasGLOverride
                        ? "Changes made in the document"
                        : "Changes Requested"}
                  </span>
                </div>
                <div className="flex w-full flex-col items-start gap-1">
                  {otherChanges.map((change, index) => (
                    <span
                      key={index}
                      className={
                        actionTypes.includes("delete_after_push_to_zoho")
                          ? "text-body font-body text-default-font whitespace-pre-line"
                          : "text-body font-body text-warning-800 whitespace-pre-line"
                      }
                    >
                      {change}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        {isInvoiceWriteOff && (
          <>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Invoice summary
              </span>
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                {otherChanges.map((change, index) => (
                  <span
                    key={index}
                    className="text-body font-body text-default-font whitespace-pre-line"
                  >
                    {change}
                  </span>
                ))}
              </div>
            </div>
            {entityId && (
              <>
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Document
                  </span>
                  <div className="flex w-full items-center justify-between gap-3 rounded-md bg-neutral-50 px-3 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600 flex-none" />
                      <div className="flex min-w-0 flex-col items-start gap-1">
                        <span className="text-body-bold font-body-bold text-default-font truncate">
                          {finalFileName || "document.pdf"}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {finalDocumentLoading ? "Loading..." : "Ready"}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={() => {
                        const id = String(entityId);
                        const path =
                          approval.entityType === "invoice"
                            ? "/invoices/review"
                            : "/bills/review";
                        navigate(
                          `${path}?documentId=${encodeURIComponent(id)}`,
                          {
                            state: { documentId: id },
                          },
                        );
                      }}
                    >
                      Open in review
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {!isCreateInvoiceOrBill &&
          !isInvoiceWriteOff &&
          !(approval.entityType === "gl" && isBulkUploadDetail) && (
          <>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {approval.entityType === "bank_transaction" ? (
                <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                  Bank Transaction - {approval.actionName} Details
                </span>
              ) : approval.entityType === "bank_account" ? (
                <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                  {getBankAccountApprovalTitle(approval.actionCode, rawJson)}{" "}
                  Details
                </span>
              ) : approval.entityType === "gl" ? (
                <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                  General Ledger Details
                </span>
              ) : (
                <span className="text-body-bold font-body-bold text-default-font">
                  {getApprovalEntityDisplayName(
                    approval.entityType,
                    "header",
                  )}{" "}
                  Details
                  {(approval.entityType === "invoice" ||
                    approval.entityType === "bill") &&
                    (actionTypes.includes("edit") ||
                      actionTypes.includes("update")) && (
                      <span className="text-body font-body text-subtext-color">
                        {" "}
                        (Before requested edits)
                      </span>
                    )}
                </span>
              )}
              <div className="flex w-full flex-col items-start gap-2">
                {/* GL request: read-only fields */}
                {approval.entityType === "gl" && rawJson && !isBulkUploadDetail && (
                  <>
                    {(() => {
                      const glAT = approval.changeDetails?.action_types || [];
                      const glAction = glAT.includes("delete")
                        ? "Delete GL"
                        : glAT.includes("edit") || glAT.includes("update")
                          ? "Update GL"
                          : "Create a GL";
                      const isDelete = glAT.includes("delete");
                      return (
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Action
                          </span>
                          <span
                            className={`text-body font-body font-semibold ${isDelete ? "text-error-600" : "text-default-font"}`}
                          >
                            {glAction}
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Account name
                      </span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.account_name ?? "—")}
                      </span>
                    </div>
                    {rawJson.account_type && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Account type
                        </span>
                        <span className="text-body font-body text-default-font">
                          {String(rawJson.account_type)
                            .split("_")
                            .map(
                              (w: string) =>
                                w.charAt(0).toUpperCase() + w.slice(1),
                            )
                            .join(" ")}
                        </span>
                      </div>
                    )}
                    {rawJson.parent_account_id && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Parent account
                        </span>
                        <span className="text-body font-body text-default-font">
                          {String(
                            rawJson.parent_account_name ??
                              rawJson.parent_account_id,
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Use in invoices
                      </span>
                      <span className="text-body font-body text-default-font">
                        {rawJson.invoice ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Use in bills
                      </span>
                      <span className="text-body font-body text-default-font">
                        {rawJson.bill ? "Yes" : "No"}
                      </span>
                    </div>
                    {(() => {
                      const glAT = approval.changeDetails?.action_types || [];
                      const fc = approval.changeDetails?.field_changes as
                        | Array<{ field: string; old: string; new: string }>
                        | undefined;
                      if (
                        !(glAT.includes("edit") || glAT.includes("update")) ||
                        !fc ||
                        fc.length === 0
                      )
                        return null;
                      const fieldLabels: Record<string, string> = {
                        account_name: "Account Name",
                        account_type: "Account Type",
                        parent_account_id: "Parent Account",
                        invoice: "Use in Invoices",
                        bill: "Use in Bills",
                      };
                      const fmtType = (v: string) =>
                        v
                          .split("_")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ");
                      const fmtVal = (field: string, v: string) => {
                        if (field === "account_type") return fmtType(v);
                        if (field === "invoice" || field === "bill")
                          return v === "true" ? "Yes" : "No";
                        return v || "None";
                      };
                      return (
                        <>
                          <div className="w-full h-px bg-neutral-border" />
                          <span className="text-body-bold font-body-bold text-default-font">
                            Changes Requested
                          </span>
                          <div
                            className="grid w-full gap-y-2"
                            style={{
                              gridTemplateColumns: "1fr auto 1.25rem auto",
                            }}
                          >
                            {fc.map((change, i) => (
                              <React.Fragment key={i}>
                                <span className="text-body font-body text-subtext-color">
                                  {fieldLabels[change.field] ?? change.field}
                                </span>
                                <span className="text-body font-body text-subtext-color text-right pr-1">
                                  {fmtVal(change.field, change.old)}
                                </span>
                                <span className="text-body font-body text-subtext-color text-center">
                                  →
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {fmtVal(change.field, change.new)}
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
                {/* Bank account request: read-only account name, type, number, currency */}
                {approval.entityType === "bank_account" && rawJson && (
                  <>
                    {(() => {
                      const ac = (
                        approval.actionCode || "create"
                      ).toLowerCase();
                      const label =
                        ac === "delete"
                          ? "Delete account"
                          : ac === "update"
                            ? "Update account"
                            : "Create account";
                      const isDelete = ac === "delete";
                      return (
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Action
                          </span>
                          <span
                            className={`text-body font-body font-semibold ${isDelete ? "text-error-600" : "text-default-font"}`}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Account name
                      </span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.account_name ?? "—")}
                      </span>
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Account type
                      </span>
                      <span className="text-body font-body text-default-font">
                        {rawJson.account_type === "credit" ||
                        rawJson.account_type === "credit_card"
                          ? "Credit card"
                          : "Bank account"}
                      </span>
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Account number
                      </span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.account_number ?? "—")}
                      </span>
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Currency
                      </span>
                      <span className="text-body font-body text-default-font">
                        {[rawJson.currency_code, rawJson.currency_name]
                          .filter(Boolean)
                          .join(" – ") || String(rawJson.currency_id ?? "—")}
                      </span>
                    </div>
                    {rawJson.opening_balance != null &&
                      rawJson.opening_balance !== "" &&
                      !Number.isNaN(Number(rawJson.opening_balance)) && (
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Opening balance
                          </span>
                          <span className="text-body font-body text-default-font">
                            {Number(rawJson.opening_balance).toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </span>
                        </div>
                      )}
                    {rawJson.opening_balance_date != null &&
                      String(rawJson.opening_balance_date).trim() !== "" && (
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Opening balance date
                          </span>
                          <span className="text-body font-body text-default-font">
                            {formatDateDDMMYYYY(
                              String(rawJson.opening_balance_date).trim(),
                            )}
                          </span>
                        </div>
                      )}

                    {(() => {
                      const ac = (
                        approval.actionCode || "create"
                      ).toLowerCase();
                      if (ac !== "update") return null;
                      const original = approval.changeDetails?.original_json as
                        | Record<string, unknown>
                        | undefined;
                      if (!original) return null;

                      const next = rawJson as Record<string, unknown>;
                      const pick = (v: unknown) =>
                        v == null ? "—" : String(v);
                      const fmtType = (v: unknown) => {
                        const s = String(v ?? "");
                        if (s === "credit" || s === "credit_card")
                          return "Credit card";
                        if (s === "bank") return "Bank account";
                        return s || "—";
                      };

                      const diffs: Array<{
                        label: string;
                        oldVal: string;
                        newVal: string;
                      }> = [];
                      const cmp = (
                        key: string,
                        label: string,
                        fmt: (v: unknown) => string = pick,
                      ) => {
                        if (!Object.prototype.hasOwnProperty.call(next, key))
                          return;
                        const o = fmt((original as any)[key]);
                        const n = fmt((next as any)[key]);
                        if (o !== n)
                          diffs.push({ label, oldVal: o, newVal: n });
                      };

                      cmp("account_name", "Account name");
                      cmp("account_type", "Account type", fmtType);
                      cmp("account_number", "Account number");
                      cmp("currency_code", "Currency");
                      cmp("opening_balance", "Opening balance", (v) =>
                        v == null || v === ""
                          ? "Empty"
                          : Number(v).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }),
                      );
                      cmp(
                        "opening_balance_date",
                        "Opening balance date",
                        (v) =>
                          v ? formatDateDDMMYYYY(String(v).trim()) : "Empty",
                      );

                      if (diffs.length === 0) return null;
                      return (
                        <>
                          <div className="w-full h-px bg-neutral-border" />
                          <span className="text-body-bold font-body-bold text-default-font">
                            Changes Requested
                          </span>
                          <div className="flex w-full flex-col gap-3">
                            {diffs.map((d, i) => (
                              <div
                                key={i}
                                className="flex w-full items-start justify-between gap-6"
                              >
                                <span className="text-body font-body text-subtext-color">
                                  {d.label}
                                </span>
                                <div className="flex flex-col items-end">
                                  <span className="text-body font-body text-subtext-color">
                                    {d.oldVal}
                                  </span>
                                  <span className="text-body font-body text-subtext-color">
                                    →
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {d.newVal}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
                {/* Invoice / Bill / Credit Note / Debit Note: number at top */}
                {(approval.entityType === "invoice" ||
                  approval.entityType === "bill" ||
                  approval.entityType === "credit_note" ||
                  approval.entityType === "debit_note") && (
                  <>
                    {(approval.entityType === "invoice" ||
                      approval.entityType === "credit_note") && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          {approval.entityType === "invoice"
                            ? "Invoice Number"
                            : "Reference Number"}
                        </span>
                        <span className="text-body font-body text-default-font">
                          {displayJson?.invoice_number ||
                            displayJson?.reference_number ||
                            "N/A"}
                        </span>
                      </div>
                    )}
                    {(approval.entityType === "bill" ||
                      approval.entityType === "debit_note") && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          {approval.entityType === "bill"
                            ? "Bill Number"
                            : "Reference Number"}
                        </span>
                        <span className="text-body font-body text-default-font">
                          {displayJson?.bill_number ||
                            displayJson?.invoice_number ||
                            displayJson?.reference_number ||
                            "N/A"}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {(approval.entityType === "invoice" ||
                  approval.entityType === "credit_note") && (
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Customer
                    </span>
                    <span className="text-body font-body text-default-font">
                      {getCustomerOrVendorName(
                        displayJson,
                        approval.entityType,
                      )}
                    </span>
                  </div>
                )}
                {(approval.entityType === "bill" ||
                  approval.entityType === "debit_note") && (
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Vendor
                    </span>
                    <span className="text-body font-body text-default-font">
                      {getCustomerOrVendorName(
                        displayJson,
                        approval.entityType,
                      )}
                    </span>
                  </div>
                )}
                {/* Date (Invoice Date / Bill Date etc.) - right after Customer/Vendor; not shown for bank_account or gl */}
                {approval.entityType !== "journal_template" &&
                  approval.entityType !== "bank_account" &&
                  approval.entityType !== "gl" && (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        {approval.entityType === "invoice"
                          ? "Invoice Date"
                          : approval.entityType === "bill"
                            ? "Bill Date"
                            : approval.entityType === "credit_note"
                              ? "Credit Note Date"
                              : approval.entityType === "debit_note"
                                ? "Debit Note Date"
                                : isJournalLikeApproval
                                  ? "Journal Date"
                                  : approval.entityType === "bank_transaction"
                                    ? "Transaction Date"
                                    : "Date"}
                      </span>
                      <span className="text-body font-body text-default-font">
                        {approval.entityType === "month_end_checklist_item" ||
                        approval.entityType === "customer" ||
                        approval.entityType === "vendor"
                          ? formatDate(approval.createdAt)
                          : isJournalLikeApproval
                            ? formatDate(
                                rawJson?.journal?.date ??
                                  rawJson?.date ??
                                  approval.changeDetails?.original_json?.date,
                              )
                            : formatDate(
                                displayJson?.date ||
                                  displayJson?.transaction_date ||
                                  displayJson?.invoice_date ||
                                  displayJson?.bill_date ||
                                  (approval.entityType === "bank_transaction" &&
                                  displayJson?.categorize_in_zoho_json?.date
                                    ? displayJson.categorize_in_zoho_json.date
                                    : undefined),
                              )}
                      </span>
                    </div>
                  )}
                {approval.entityType === "bank_transaction" && (
                  <>
                    {rawJson?.category_type === "customer_payment" &&
                      rawJson?.customer_name && (
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Customer
                          </span>
                          <span className="text-body font-body text-default-font">
                            {rawJson.customer_name}
                          </span>
                        </div>
                      )}
                    {rawJson?.category_type === "vendor_payment" &&
                      rawJson?.vendor_name && (
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Vendor
                          </span>
                          <span className="text-body font-body text-default-font">
                            {rawJson.vendor_name}
                          </span>
                        </div>
                      )}
                    {Array.isArray(rawJson?.split_items) && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Transaction Amount
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.currency_symbol || "₹"}{" "}
                          {Number(rawJson.transaction_amount || 0).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </span>
                      </div>
                    )}
                    {rawJson?.categorize_in_zoho_json && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Amount
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatAmount(
                            rawJson,
                            approval.entityType,
                            approval.changeDetails,
                          )}
                        </span>
                      </div>
                    )}
                    {rawJson?.categorize_in_zoho_json?.currency_symbol && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Currency
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.categorize_in_zoho_json.currency_symbol}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {isJournalLikeApproval &&
                  (approval.changeDetails?.original_json ||
                    (approval.changeDetails?.action_types?.includes(
                      "create_recurring",
                    ) &&
                      approval.changeDetails?.raw_json) ||
                    (approval.changeDetails?.action_types?.includes(
                      "ap_ar_create",
                    ) &&
                      approval.changeDetails?.raw_json) ||
                    (approval.changeDetails?.action_types?.includes(
                      "ap_ar_delete",
                    ) &&
                      approval.changeDetails?.raw_json)) && (
                    <>
                      {(() => {
                        // Draft flow or create_recurring only: show updated journal from raw_json. Otherwise show original.
                        const journalDisplayOriginal =
                          isJournalDraftFlow || hasCreateRecurringOnly
                            ? approval.changeDetails.raw_json
                            : approval.changeDetails.original_json;
                        const journalDisplayRaw =
                          approval.changeDetails.raw_json;
                        const journalInfo = formatJournalInfo(
                          journalDisplayOriginal,
                          journalDisplayRaw,
                        );
                        return (
                          <>
                            <div className="flex w-full items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Journal Number
                              </span>
                              <span className="text-body font-body text-default-font">
                                {journalInfo.journalNumber}
                              </span>
                            </div>
                            <div className="flex w-full items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Reference Number
                              </span>
                              <span className="text-body font-body text-default-font">
                                {journalInfo.referenceNumber}
                              </span>
                            </div>
                            {/* ZOUR-65 F4: which GST registration the approver
                                is signing off on. Only where there is a choice. */}
                            {hasMultipleLocations && (
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Location
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {describeLocation(journalInfo.locationId)}
                                </span>
                              </div>
                            )}
                            {(journalDisplayOriginal?.date ||
                              approval.changeDetails.raw_json?.journal?.date ||
                              approval.changeDetails.raw_json?.date) && (
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Journal Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    journalDisplayOriginal?.date ??
                                      approval.changeDetails.raw_json?.journal
                                        ?.date ??
                                      approval.changeDetails.raw_json?.date,
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="flex w-full items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Currency
                              </span>
                              <span className="text-body font-body text-default-font">
                                {journalInfo.currencySymbol}
                              </span>
                            </div>
                            <div className="flex w-full items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Method
                              </span>
                              <span className="text-body font-body text-default-font">
                                {journalInfo.method}
                              </span>
                            </div>
                            <div className="flex w-full items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Total Debit
                              </span>
                              <span className="text-body font-body text-default-font">
                                {journalInfo.currencySymbol}{" "}
                                {journalInfo.totalDebit}
                              </span>
                            </div>
                            <div className="flex w-full items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Total Credit
                              </span>
                              <span className="text-body font-body text-default-font">
                                {journalInfo.currencySymbol}{" "}
                                {journalInfo.totalCredit}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                      {(approval.changeDetails?.action_types?.includes(
                        "update_after_push",
                      ) ||
                        approval.changeDetails?.action_types?.includes(
                          "month_close",
                        )) &&
                        approval.changeDetails?.original_json && (
                          <>
                            {(() => {
                              const totals = getJournalOriginalAndNewTotals(
                                approval.changeDetails.original_json,
                                approval.changeDetails.raw_json,
                              );
                              return (
                                <>
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Original Debit
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {totals.currencySymbol}{" "}
                                      {totals.originalDebit}
                                    </span>
                                  </div>
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Original Credit
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {totals.currencySymbol}{" "}
                                      {totals.originalCredit}
                                    </span>
                                  </div>
                                  {totals.debitChanged && (
                                    <div className="flex w-full items-center justify-between">
                                      <span className="text-body font-body text-subtext-color">
                                        New Debit
                                      </span>
                                      <span className="text-body font-body text-default-font">
                                        {totals.currencySymbol}{" "}
                                        {totals.newDebit}
                                      </span>
                                    </div>
                                  )}
                                  {totals.creditChanged && (
                                    <div className="flex w-full items-center justify-between">
                                      <span className="text-body font-body text-subtext-color">
                                        New Credit
                                      </span>
                                      <span className="text-body font-body text-default-font">
                                        {totals.currencySymbol}{" "}
                                        {totals.newCredit}
                                      </span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                    </>
                  )}
                {/* Credits Applied section for credit notes */}
                {approval.entityType === "credit_note" &&
                  approval.changeDetails?.original_credit_note && (
                    <>
                      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                      <div className="flex w-full flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Credits Applied
                        </span>
                        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                          {formatCreditsApplied(
                            approval.changeDetails.original_credit_note,
                            rawJson,
                          ).length > 0 ? (
                            formatCreditsApplied(
                              approval.changeDetails.original_credit_note,
                              rawJson,
                            ).map((info, index) => (
                              <span
                                key={index}
                                className={`text-body font-body ${
                                  index === 0
                                    ? "text-default-font font-semibold"
                                    : "text-subtext-color"
                                }`}
                              >
                                {info}
                              </span>
                            ))
                          ) : (
                            <span className="text-body font-body text-subtext-color">
                              No credits applied
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                {isJournalLikeApproval &&
                  (hasCreateRecurringOnly || isJournalDraftFlow) &&
                  approval.changeDetails?.raw_json?.line_items &&
                  Array.isArray(approval.changeDetails.raw_json.line_items) &&
                  approval.changeDetails.raw_json.line_items.length > 0 && (
                    <>
                      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                      <div className="flex w-full flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Journal Line Items
                        </span>
                        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                          {approval.changeDetails.raw_json.line_items.map(
                            (item: any, index: number) => {
                              const amount = parseFloat(item.amount || 0);
                              const isDebitOrCredit = (
                                item.is_debit_or_credit || ""
                              )
                                .toString()
                                .toLowerCase();
                              const accountName = item.account_name || "N/A";
                              const contactName = item.contact_name || null;
                              const description = item.description || "";
                              const currencySymbol =
                                approval.changeDetails?.raw_json
                                  ?.currency_symbol ||
                                approval.changeDetails?.raw_json?.journal
                                  ?.currency_symbol ||
                                "₹";
                              return (
                                <div
                                  key={index}
                                  className="flex w-full flex-col items-start gap-2"
                                >
                                  <div className="flex w-full items-start justify-between">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-body-bold font-body-bold text-default-font">
                                        Line Item {index + 1}
                                      </span>
                                      <span className="text-caption font-caption text-subtext-color">
                                        Account: {accountName}
                                      </span>
                                      {contactName && (
                                        <span className="text-caption font-caption text-subtext-color">
                                          Contact: {contactName}
                                        </span>
                                      )}
                                      {description && (
                                        <span className="text-caption font-caption text-subtext-color">
                                          {description}
                                        </span>
                                      )}
                                      {item.reference_display && (
                                        <span className="text-caption font-caption text-subtext-color">
                                          Invoice / Bill:{" "}
                                          {item.reference_display}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {isDebitOrCredit === "debit" &&
                                        amount > 0 && (
                                          <span className="text-body-bold font-body-bold text-default-font">
                                            {currencySymbol}{" "}
                                            {amount.toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}{" "}
                                            (Debit)
                                          </span>
                                        )}
                                      {isDebitOrCredit === "credit" &&
                                        amount > 0 && (
                                          <span className="text-body-bold font-body-bold text-default-font">
                                            {currencySymbol}{" "}
                                            {amount.toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}{" "}
                                            (Credit)
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                  {(() => {
                                    const rate = parseFloat(
                                      item.exchange_rate ?? 0,
                                    );
                                    if (rate > 0 && amount > 0) {
                                      const contactSymbol =
                                        item.contact_currency_symbol ||
                                        currencySymbol;
                                      const amountInContactCurrency =
                                        amount / rate;
                                      const fmt = (n: number) =>
                                        n.toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        });
                                      return (
                                        <div className="mt-2 flex flex-col gap-1 border-t border-neutral-200 pt-2">
                                          <span className="text-caption font-caption text-subtext-color">
                                            Exchange Rate: {fmt(rate)}
                                          </span>
                                          <span className="text-caption font-caption text-subtext-color">
                                            Amount ({contactSymbol}):{" "}
                                            {fmt(amountInContactCurrency)}
                                          </span>
                                          <span className="text-caption font-caption text-subtext-color">
                                            Amount in INR (₹): {fmt(amount)}
                                          </span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                  {index <
                                    (approval.changeDetails?.raw_json
                                      ?.line_items?.length ?? 0) -
                                      1 && (
                                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                                  )}
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    </>
                  )}
                {isJournalLikeApproval &&
                  hasRecurringParamsInRawJson(
                    approval.changeDetails?.raw_json,
                  ) &&
                  (hasCreateRecurringOnly || isJournalDraftFlow) && (
                    <>
                      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                      <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Recurring Journal
                        </span>
                        {formatRecurringJournalFieldsOnly(
                          approval.changeDetails?.raw_json,
                        ).map((line, idx) => (
                          <span
                            key={idx}
                            className="text-body font-body text-default-font whitespace-pre-line"
                          >
                            {line}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                {isJournalLikeApproval &&
                  !hasCreateRecurringOnly &&
                  !isJournalDraftFlow &&
                  approval.changeDetails?.original_json?.line_items &&
                  Array.isArray(
                    approval.changeDetails.original_json.line_items,
                  ) &&
                  approval.changeDetails.original_json.line_items.length >
                    0 && (
                    <>
                      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                      <div className="flex w-full flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Original Journal Line Items
                        </span>
                        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                          {approval.changeDetails.original_json.line_items.map(
                            (item: any, index: number) => {
                              const amount = parseFloat(item.amount || 0);
                              const isDebitOrCredit =
                                item.is_debit_or_credit?.toLowerCase();
                              const accountName = item.account_name || "N/A";
                              const contactName = item.contact_name || null;
                              const description = item.description || "";
                              const currencySymbol =
                                approval.changeDetails?.original_json
                                  ?.currency_symbol || "₹";

                              return (
                                <div
                                  key={index}
                                  className="flex w-full flex-col items-start gap-2"
                                >
                                  <div className="flex w-full items-start justify-between">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-body-bold font-body-bold text-default-font">
                                        Line Item {index + 1}
                                      </span>
                                      <span className="text-caption font-caption text-subtext-color">
                                        Account: {accountName}
                                      </span>
                                      {contactName && (
                                        <span className="text-caption font-caption text-subtext-color">
                                          Contact: {contactName}
                                        </span>
                                      )}
                                      {description && (
                                        <span className="text-caption font-caption text-subtext-color">
                                          {description}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {isDebitOrCredit === "debit" &&
                                        amount > 0 && (
                                          <span className="text-body-bold font-body-bold text-default-font">
                                            {currencySymbol}{" "}
                                            {amount.toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}{" "}
                                            (Debit)
                                          </span>
                                        )}
                                      {isDebitOrCredit === "credit" &&
                                        amount > 0 && (
                                          <span className="text-body-bold font-body-bold text-default-font">
                                            {currencySymbol}{" "}
                                            {amount.toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}{" "}
                                            (Credit)
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    </>
                  )}
                {/* Journal Template Details - Show when entity is journal_template and action is cancel */}
                {approval.entityType === "journal_template" &&
                  approval.changeDetails?.raw_json && (
                    <>
                      {(() => {
                        const templateData = approval.changeDetails.raw_json;
                        const actionTypes =
                          approval.changeDetails.action_types || [];
                        const hasCancelAction = actionTypes.includes(
                          "cancel_recurring_before_end",
                        );

                        if (!hasCancelAction) return null;

                        const currencySymbol =
                          templateData.currency_symbol || "₹";
                        const formatFrequency = (freq: string) => {
                          const freqMap: Record<string, string> = {
                            start_of_month: "Monthly (Start)",
                            end_of_month: "Monthly (End)",
                            daily: "Daily",
                            weekly: "Weekly",
                          };
                          return freqMap[freq] || freq;
                        };

                        return (
                          <>
                            {/* Template Basic Info */}
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                            <div className="flex w-full flex-col items-start gap-3">
                              <span className="text-body-bold font-body-bold text-default-font">
                                Template Details
                              </span>
                              <div className="flex w-full flex-col items-start gap-2">
                                {templateData.type && (
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Type
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {templateData.type === "both"
                                        ? "Both"
                                        : templateData.type}
                                    </span>
                                  </div>
                                )}
                                {templateData.notes && (
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Notes
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {templateData.notes}
                                    </span>
                                  </div>
                                )}
                                {templateData.recurrence_frequency && (
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Frequency
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {formatFrequency(
                                        templateData.recurrence_frequency,
                                      )}
                                    </span>
                                  </div>
                                )}
                                {templateData.starts_on && (
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Starts On
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {formatDate(templateData.starts_on)}
                                    </span>
                                  </div>
                                )}
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Ends On
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {templateData.never_expires === "1" ||
                                    templateData.never_expires === 1 ||
                                    templateData.never_expires === true
                                      ? "Never Expires"
                                      : templateData.ends_on
                                        ? formatDate(templateData.ends_on)
                                        : "N/A"}
                                  </span>
                                </div>
                                {templateData.created_at && (
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Created At
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {formatDate(templateData.created_at)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Template Line Items */}
                            {templateData.line_items &&
                              Array.isArray(templateData.line_items) &&
                              templateData.line_items.length > 0 && (
                                <>
                                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                                  <div className="flex w-full flex-col items-start gap-3">
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      Template Line Items
                                    </span>
                                    <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                                      {templateData.line_items.map(
                                        (item: any, index: number) => {
                                          const amount = parseFloat(
                                            item.amount || 0,
                                          );
                                          const isDebitOrCredit =
                                            item.is_debit_or_credit?.toLowerCase();
                                          const accountName =
                                            item.account_name || "N/A";
                                          const contactName =
                                            item.contact_name || null;
                                          const description =
                                            item.description || "";

                                          return (
                                            <div
                                              key={index}
                                              className="flex w-full flex-col items-start gap-2"
                                            >
                                              <div className="flex w-full items-start justify-between">
                                                <div className="flex flex-col items-start gap-1">
                                                  <span className="text-body-bold font-body-bold text-default-font">
                                                    Line Item {index + 1}
                                                  </span>
                                                  <span className="text-caption font-caption text-subtext-color">
                                                    Account: {accountName}
                                                  </span>
                                                  {contactName && (
                                                    <span className="text-caption font-caption text-subtext-color">
                                                      Contact: {contactName}
                                                    </span>
                                                  )}
                                                  {description && (
                                                    <span className="text-caption font-caption text-subtext-color">
                                                      {description}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                  {isDebitOrCredit ===
                                                    "debit" &&
                                                    amount > 0 && (
                                                      <span className="text-body-bold font-body-bold text-default-font">
                                                        {currencySymbol}{" "}
                                                        {amount.toLocaleString(
                                                          "en-US",
                                                          {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                          },
                                                        )}{" "}
                                                        (Debit)
                                                      </span>
                                                    )}
                                                  {isDebitOrCredit ===
                                                    "credit" &&
                                                    amount > 0 && (
                                                      <span className="text-body-bold font-body-bold text-default-font">
                                                        {currencySymbol}{" "}
                                                        {amount.toLocaleString(
                                                          "en-US",
                                                          {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                          },
                                                        )}{" "}
                                                        (Credit)
                                                      </span>
                                                    )}
                                                </div>
                                              </div>
                                              {index <
                                                templateData.line_items.length -
                                                  1 && (
                                                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                                              )}
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}

                            {/* Generated Journals - Show only count */}
                            {templateData.total_generated_count &&
                              templateData.total_generated_count > 0 && (
                                <>
                                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                                  <div className="flex w-full items-center justify-between">
                                    <span className="text-body font-body text-subtext-color">
                                      Generated Journals
                                    </span>
                                    <span className="text-body font-body text-default-font">
                                      {templateData.total_generated_count}
                                    </span>
                                  </div>
                                </>
                              )}
                          </>
                        );
                      })()}
                    </>
                  )}

                {/* Removed "Changes Requested" section for journals in view-only mode */}
                {/* Show notes for journals if they exist in raw_json (new journal data) - hide for push_to_zoho_without_attachment */}
                {isJournalLikeApproval &&
                  !actionTypes.includes("push_to_zoho_without_attachment") &&
                  (() => {
                    const notes =
                      rawJson?.journal?.notes || rawJson?.notes || null;
                    if (
                      !notes ||
                      notes === "N/A" ||
                      notes === "(empty)" ||
                      notes.trim() === ""
                    ) {
                      return null;
                    }
                    return (
                      <>
                        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                        <div className="flex w-full flex-col items-start gap-3">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Notes
                          </span>
                          <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                            <span className="text-body font-body text-default-font whitespace-pre-line">
                              {notes}
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                {(approval.entityType === "credit_note" ||
                  approval.entityType === "debit_note") && (
                  <>
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Reference Number
                      </span>
                      <span className="text-body font-body text-default-font">
                        {rawJson?.reference_number || "N/A"}
                      </span>
                    </div>
                    {rawJson?.currency_symbol && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Currency
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.currency_symbol}
                        </span>
                      </div>
                    )}
                    {rawJson?.gst_no && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          {approval.entityType === "credit_note"
                            ? "Customer GSTIN"
                            : "Vendor GSTIN"}
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.gst_no}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {/* Invoice / Bill / Credit Note / Debit Note: due date, subtotal, tax total, total (number and date are above) */}
                {(approval.entityType === "invoice" ||
                  approval.entityType === "bill" ||
                  approval.entityType === "credit_note" ||
                  approval.entityType === "debit_note") &&
                  displayJsonRecord && (
                    <InvoiceBillFinancialTotals
                      displayJson={displayJsonRecord}
                      moduleKind={
                        approval.entityType === "bill" ? "bill" : "invoice"
                      }
                      currencySymbol={currencySymbol}
                      resolvedGstTreatment={
                        approval.entityType === "bill"
                          ? resolvedGstTreatment
                          : undefined
                      }
                      resolvedGstCategory={
                        approval.entityType === "bill"
                          ? resolvedGstCategory
                          : undefined
                      }
                      formatDate={formatDate}
                    />
                  )}

                {approval.reviewerName != null &&
                  approval.reviewerName !== "" && (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Request sent to
                      </span>
                      <span className="text-body font-body text-default-font">
                        {approval.reviewerName}
                      </span>
                    </div>
                  )}
              </div>
            </div>
            {/* Line Items section for credit notes - moved after Raised by */}
            {approval.entityType === "credit_note" &&
              approval.changeDetails?.original_credit_note?.credit_note
                ?.json && (
                <>
                  {(() => {
                    const creditNoteJson =
                      typeof approval.changeDetails.original_credit_note
                        .credit_note.json === "string"
                        ? JSON.parse(
                            approval.changeDetails.original_credit_note
                              .credit_note.json,
                          )
                        : approval.changeDetails.original_credit_note
                            .credit_note.json;
                    const creditLineItems = creditNoteJson.line_items || [];
                    const currencySymbol =
                      creditNoteJson.currency_symbol || "₹";

                    if (creditLineItems.length === 0) {
                      return null;
                    }

                    return (
                      <>
                        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                        <div className="flex w-full flex-col items-start gap-3">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Line Items
                          </span>
                          <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-3">
                            {creditLineItems.map((item: any, index: number) => {
                              const lineTotal = getLineItemDisplayTotal(item);
                              const totalAmount =
                                getLineItemInclusiveTotal(item);
                              const taxAmount = getLineItemTaxAmount(item);
                              const accountName = item.account_name || "N/A";

                              return (
                                <div
                                  key={index}
                                  className="flex w-full flex-col items-start gap-2"
                                >
                                  <div className="flex w-full items-start justify-between gap-3">
                                    <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                                      <span className="text-body-bold font-body-bold text-default-font break-words">
                                        {item.description ||
                                          item.name ||
                                          `Item ${index + 1}`}
                                      </span>
                                      <span className="text-caption font-caption text-subtext-color">
                                        GL: {accountName}
                                      </span>
                                    </div>
                                    <span className="text-body font-body text-default-font shrink-0 whitespace-nowrap">
                                      {currencySymbol}{" "}
                                      {lineTotal.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                  {shouldShowLineItemTaxRow(item) && (
                                    <>
                                      <div className="flex w-full items-center justify-between">
                                        <span className="text-caption font-caption text-subtext-color">
                                          {getLineItemTaxRowLabel(item)}
                                        </span>
                                        <span className="text-caption font-caption text-default-font shrink-0 whitespace-nowrap">
                                          {currencySymbol}{" "}
                                          {taxAmount.toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                      <div className="flex w-full items-center justify-between">
                                        <span className="text-caption font-caption text-subtext-color">
                                          Total Amount
                                        </span>
                                        <span className="text-caption font-caption text-default-font shrink-0 whitespace-nowrap">
                                          {currencySymbol}{" "}
                                          {totalAmount.toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            {/* Line Items for other entity types (not credit notes). Skip for write-off - summary shown above */}
            {lineItems.length > 0 &&
              approval.entityType !== "credit_note" &&
              !isInvoiceWriteOff && (
                <>
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                  <div className="flex w-full flex-col items-start gap-3">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Line Items
                      </span>
                      <FeatherChevronDown className="text-body font-body text-neutral-600" />
                    </div>
                    <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-3">
                      {lineItems.slice(0, 3).map((item: any, index: number) => {
                        const lineTotal = getLineItemDisplayTotal(item);
                        const totalAmount = getLineItemInclusiveTotal(item);
                        const taxAmount = getLineItemTaxAmount(item);
                        const accountName = item.account_name || "N/A";

                        return (
                          <div
                            key={index}
                            className="flex w-full flex-col items-start gap-2"
                          >
                            <div className="flex w-full items-start justify-between gap-3">
                              <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                                <span className="text-body-bold font-body-bold text-default-font break-words">
                                  {item.name ||
                                    item.description ||
                                    `Item ${index + 1}`}
                                </span>
                                <span className="text-caption font-caption text-subtext-color">
                                  GL: {accountName}
                                </span>
                              </div>
                              <span className="text-body font-body text-default-font shrink-0 whitespace-nowrap">
                                {currencySymbol}{" "}
                                {lineTotal.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                            {shouldShowLineItemTaxRow(item) && (
                              <>
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-caption font-caption text-subtext-color">
                                    {getLineItemTaxRowLabel(item)}
                                  </span>
                                  <span className="text-caption font-caption text-default-font shrink-0 whitespace-nowrap">
                                    {currencySymbol}{" "}
                                    {taxAmount.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-caption font-caption text-subtext-color">
                                    Total Amount
                                  </span>
                                  <span className="text-caption font-caption text-default-font shrink-0 whitespace-nowrap">
                                    {currencySymbol}{" "}
                                    {totalAmount.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                      {lineItems.length > 3 && (
                        <div className="text-caption font-caption text-subtext-color">
                          +{lineItems.length - 3} more items
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            {approval.entityType === "bank_transaction" && rawJson && (
              <>
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Transaction Details
                  </span>
                  <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                    {Array.isArray(rawJson.split_items) && (
                      <>
                        <div className="flex w-full flex-col gap-2">
                          <span className="text-heading-4 font-heading-4 text-default-font">
                            Split Transaction Amount
                          </span>
                          <span className="text-caption font-caption text-subtext-color">
                            {rawJson.split_items.length} split item
                            {rawJson.split_items.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="flex w-full flex-col gap-2">
                          {rawJson.split_items.map((item: any, index: number) => (
                            <div
                              key={`split-item-${index}`}
                              className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2"
                            >
                              <div className="flex w-full items-center justify-between gap-3">
                                <span className="text-body-bold font-body-bold text-default-font">
                                  Item {index + 1}
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol || "₹"}{" "}
                                  {Number(item.amount || 0).toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}
                                </span>
                              </div>
                              <div className="flex w-full items-start justify-between gap-3">
                                <span className="text-body font-body text-subtext-color">
                                  Description
                                </span>
                                <span className="text-body font-body text-default-font text-right">
                                  {item.description || "N/A"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {/* Customer Payment - handle both match (categorize_in_zoho_json) and unmatch (category_details) */}
                    {rawJson.category_type === "customer_payment" &&
                      ((rawJson.categorize_in_zoho_json?.invoices &&
                        rawJson.categorize_in_zoho_json.invoices.length > 0) ||
                        (rawJson.category_details?.invoices &&
                          rawJson.category_details.invoices.length > 0)) && (
                        <>
                          <div className="flex w-full flex-col gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Customer Payment
                              </span>
                              {(rawJson.category_details?.customer_name ||
                                rawJson.customer_name) && (
                                <Badge className="border-success-700 bg-success-700 text-white [&>span]:text-white">
                                  {rawJson.category_details?.customer_name ||
                                    rawJson.customer_name}
                                </Badge>
                              )}
                            </div>
                            <span className="text-caption font-caption text-subtext-color">
                              Invoices Applied
                            </span>
                          </div>
                          <div className="flex w-full flex-col gap-2">
                            {(
                              rawJson.categorize_in_zoho_json?.invoices ||
                              rawJson.category_details?.invoices ||
                              []
                            )
                              .slice(0, 5)
                              .map((invoice: any, index: number) => {
                                const invoiceNumber =
                                  invoice.invoice_number ||
                                  invoice.invoice_id ||
                                  `Invoice ${index + 1}`;
                                const amountApplied = Number(
                                  invoice.amount_applied || 0,
                                );
                                const currencySymbol =
                                  rawJson.categorize_in_zoho_json
                                    ?.currency_symbol ||
                                  rawJson.category_details?.currency_symbol ||
                                  rawJson.currency_symbol ||
                                  "₹";
                                const tdsAmount =
                                  invoice.tds_tax_amount &&
                                  invoice.tds_tax_amount > 0
                                    ? Number(invoice.tds_tax_amount)
                                    : 0;

                                return (
                                  <div
                                    key={index}
                                    className="flex w-full items-center justify-between px-3 py-2 rounded border border-neutral-border bg-white"
                                  >
                                    <div className="flex flex-col gap-1">
                                      <span className="text-body-bold font-body-bold text-default-font">
                                        {invoiceNumber}
                                      </span>
                                      {tdsAmount > 0 && (
                                        <span className="text-caption font-caption text-subtext-color">
                                          TDS: {currencySymbol}
                                          {tdsAmount.toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-body-bold font-body-bold text-success-600">
                                      {currencySymbol}{" "}
                                      {amountApplied.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                          {(
                            rawJson.categorize_in_zoho_json?.invoices ||
                            rawJson.category_details?.invoices ||
                            []
                          ).length > 5 && (
                            <div className="text-caption font-caption text-subtext-color px-3">
                              +
                              {(
                                rawJson.categorize_in_zoho_json?.invoices ||
                                rawJson.category_details?.invoices ||
                                []
                              ).length - 5}{" "}
                              more invoices
                            </div>
                          )}
                          <div className="flex w-full items-center gap-2 px-3">
                            <span className="text-body font-body text-subtext-color">
                              Advance Payment:
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {rawJson.categorize_in_zoho_json
                                ?.currency_symbol ||
                                rawJson.category_details?.currency_symbol ||
                                rawJson.currency_symbol ||
                                "₹"}{" "}
                              {Number(
                                rawJson.advance_payment ||
                                  rawJson.category_details?.advance_payment ||
                                  0,
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </>
                      )}
                    {/* Vendor Payment - handle both match (categorize_in_zoho_json) and unmatch (category_details) */}
                    {rawJson.category_type === "vendor_payment" &&
                      ((rawJson.categorize_in_zoho_json?.bills &&
                        rawJson.categorize_in_zoho_json.bills.length > 0) ||
                        (rawJson.category_details?.bills &&
                          rawJson.category_details.bills.length > 0)) && (
                        <>
                          <div className="flex w-full flex-col gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Vendor Payment
                              </span>
                              {(rawJson.category_details?.vendor_name ||
                                rawJson.vendor_name) && (
                                <Badge className="border-success-700 bg-success-700 text-white [&>span]:text-white">
                                  {rawJson.category_details?.vendor_name ||
                                    rawJson.vendor_name}
                                </Badge>
                              )}
                            </div>
                            <span className="text-caption font-caption text-subtext-color">
                              Bills Applied
                            </span>
                          </div>
                          <div className="flex w-full flex-col gap-2">
                            {(
                              rawJson.categorize_in_zoho_json?.bills ||
                              rawJson.category_details?.bills ||
                              []
                            )
                              .slice(0, 5)
                              .map((bill: any, index: number) => {
                                const billNumber =
                                  bill.bill_number ||
                                  bill.bill_id ||
                                  `Bill ${index + 1}`;
                                const amountApplied = Number(
                                  bill.amount_applied || 0,
                                );
                                const currencySymbol =
                                  rawJson.categorize_in_zoho_json
                                    ?.currency_symbol ||
                                  rawJson.category_details?.currency_symbol ||
                                  rawJson.currency_symbol ||
                                  "₹";
                                return (
                                  <div
                                    key={index}
                                    className="flex w-full items-center justify-between px-3 py-2 rounded border border-neutral-border bg-white"
                                  >
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      {billNumber}
                                    </span>
                                    <span className="text-body-bold font-body-bold text-success-600">
                                      {currencySymbol}{" "}
                                      {amountApplied.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                          {(
                            rawJson.categorize_in_zoho_json?.bills ||
                            rawJson.category_details?.bills ||
                            []
                          ).length > 5 && (
                            <div className="text-caption font-caption text-subtext-color px-3">
                              +
                              {(
                                rawJson.categorize_in_zoho_json?.bills ||
                                rawJson.category_details?.bills ||
                                []
                              ).length - 5}{" "}
                              more bills
                            </div>
                          )}
                          <div className="flex w-full items-center gap-2 px-3">
                            <span className="text-body font-body text-subtext-color">
                              Advance Payment:
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {rawJson.categorize_in_zoho_json
                                ?.currency_symbol ||
                                rawJson.category_details?.currency_symbol ||
                                rawJson.currency_symbol ||
                                "₹"}{" "}
                              {Number(
                                rawJson.advance_payment ||
                                  rawJson.category_details?.advance_payment ||
                                  0,
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </>
                      )}
                    {/* Split Customer - support both category_details.splits (unmatch) and customers (match) */}
                    {rawJson.category_type === "split_customer" &&
                      (rawJson.category_details?.splits?.length > 0 ||
                        rawJson.customers?.length > 0) &&
                      (() => {
                        const splitsFromDetails =
                          rawJson.category_details?.splits ?? [];
                        const splitsFromCustomers = (
                          rawJson.customers ?? []
                        ).map((c: any) => ({
                          customer_name: c.customer_name,
                          amount:
                            (c.advance_payment ?? 0) +
                            (c.invoices ?? []).reduce(
                              (s: number, inv: any) =>
                                s + (inv.amount_applied ?? 0),
                              0,
                            ),
                          invoices: c.invoices ?? [],
                          advance_payment: c.advance_payment ?? 0,
                        }));
                        const splits =
                          splitsFromDetails.length > 0
                            ? splitsFromDetails
                            : splitsFromCustomers;
                        const totalAmount =
                          rawJson.category_details?.total_amount ??
                          rawJson.transaction_amount ??
                          splits.reduce(
                            (sum: number, s: any) => sum + (s.amount ?? 0),
                            0,
                          );
                        const currencySymbol = rawJson.currency_symbol ?? "₹";
                        return (
                          <>
                            <div className="flex w-full flex-col gap-2">
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Customer Payment (Split)
                              </span>
                              <span className="text-caption font-caption text-subtext-color">
                                Split across {splits.length} customers
                              </span>
                            </div>
                            <div className="flex w-full flex-col gap-2">
                              {splits.map((split: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex w-full flex-col gap-2 rounded border border-neutral-border bg-white px-3 py-2"
                                >
                                  <div className="flex w-full items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                      <span className="text-body-bold font-body-bold text-default-font">
                                        {split.customer_name ||
                                          `Customer ${index + 1}`}
                                      </span>
                                      <span className="text-caption font-caption text-subtext-color">
                                        {split.invoices?.length ?? 0} invoice(s)
                                        {(split.advance_payment ?? 0) > 0 &&
                                          ` · Advance: ${currencySymbol}${Number(split.advance_payment).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                      </span>
                                    </div>
                                    <span className="text-body-bold font-body-bold text-success-600">
                                      {currencySymbol}{" "}
                                      {Number(split.amount ?? 0).toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </span>
                                  </div>
                                  {split.invoices?.length > 0 && (
                                    <div className="overflow-x-auto rounded border border-neutral-border bg-neutral-50">
                                      <table className="w-full min-w-[320px]">
                                        <thead>
                                          <tr className="border-b border-neutral-border">
                                            <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                                              Invoice #
                                            </th>
                                            <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                                              Total
                                            </th>
                                            <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                                              Balance
                                            </th>
                                            <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                                              Amount applied
                                            </th>
                                            <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                                              Status
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(split.invoices ?? []).map(
                                            (inv: any, i: number) => (
                                              <tr
                                                key={i}
                                                className="border-b border-neutral-border last:border-b-0"
                                              >
                                                <td className="px-2 py-1.5 text-body text-default-font">
                                                  {inv.invoice_number ??
                                                    inv.invoice_id ??
                                                    "—"}
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                                                  {inv.currency_symbol ??
                                                    currencySymbol}{" "}
                                                  {Number(
                                                    inv.total ?? 0,
                                                  ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                                                  {inv.currency_symbol ??
                                                    currencySymbol}{" "}
                                                  {Number(
                                                    inv.balance ?? 0,
                                                  ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                                                  {inv.currency_symbol ??
                                                    currencySymbol}{" "}
                                                  {Number(
                                                    inv.amount_applied ?? 0,
                                                  ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}
                                                </td>
                                                <td className="px-2 py-1.5 text-body text-subtext-color">
                                                  {inv.status ?? "—"}
                                                </td>
                                              </tr>
                                            ),
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="flex w-full items-center justify-between px-3">
                              <span className="text-body font-body text-subtext-color">
                                Total
                              </span>
                              <span className="text-body-bold font-body-bold text-default-font">
                                {currencySymbol}{" "}
                                {Number(totalAmount).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    {/* Split Vendor - support both category_details.splits (unmatch) and vendors (match) */}
                    {rawJson.category_type === "split_vendor" &&
                      (rawJson.category_details?.splits?.length > 0 ||
                        rawJson.vendors?.length > 0) &&
                      (() => {
                        const splitsFromDetails =
                          rawJson.category_details?.splits ?? [];
                        const splitsFromVendors = (rawJson.vendors ?? []).map(
                          (v: any) => ({
                            vendor_name: v.vendor_name,
                            amount:
                              (v.advance_payment ?? 0) +
                              (v.bills ?? []).reduce(
                                (s: number, b: any) =>
                                  s + (b.amount_applied ?? 0),
                                0,
                              ),
                            bills: v.bills ?? [],
                            advance_payment: v.advance_payment ?? 0,
                          }),
                        );
                        const splits =
                          splitsFromDetails.length > 0
                            ? splitsFromDetails
                            : splitsFromVendors;
                        const totalAmount =
                          rawJson.category_details?.total_amount ??
                          rawJson.transaction_amount ??
                          splits.reduce(
                            (sum: number, s: any) => sum + (s.amount ?? 0),
                            0,
                          );
                        const currencySymbol = rawJson.currency_symbol ?? "₹";
                        return (
                          <>
                            <div className="flex w-full flex-col gap-2">
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Vendor Payment (Split)
                              </span>
                              <span className="text-caption font-caption text-subtext-color">
                                Split across {splits.length} vendors
                              </span>
                            </div>
                            <div className="flex w-full flex-col gap-2">
                              {splits.map((split: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex w-full flex-col gap-2 rounded border border-neutral-border bg-white px-3 py-2"
                                >
                                  <div className="flex w-full items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                      <span className="text-body-bold font-body-bold text-default-font">
                                        {split.vendor_name ||
                                          `Vendor ${index + 1}`}
                                      </span>
                                      <span className="text-caption font-caption text-subtext-color">
                                        {split.bills?.length ?? 0} bill(s)
                                        {(split.advance_payment ?? 0) > 0 &&
                                          ` · Advance: ${currencySymbol}${Number(split.advance_payment).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                      </span>
                                    </div>
                                    <span className="text-body-bold font-body-bold text-success-600">
                                      {currencySymbol}{" "}
                                      {Number(split.amount ?? 0).toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </span>
                                  </div>
                                  {split.bills?.length > 0 && (
                                    <div className="overflow-x-auto rounded border border-neutral-border bg-neutral-50">
                                      <table className="w-full min-w-[320px]">
                                        <thead>
                                          <tr className="border-b border-neutral-border">
                                            <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                                              Bill #
                                            </th>
                                            <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                                              Total
                                            </th>
                                            <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                                              Balance
                                            </th>
                                            <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                                              Amount applied
                                            </th>
                                            <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                                              Status
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(split.bills ?? []).map(
                                            (bill: any, i: number) => (
                                              <tr
                                                key={i}
                                                className="border-b border-neutral-border last:border-b-0"
                                              >
                                                <td className="px-2 py-1.5 text-body text-default-font">
                                                  {bill.bill_number ??
                                                    bill.bill_id ??
                                                    "—"}
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                                                  {bill.currency_symbol ??
                                                    currencySymbol}{" "}
                                                  {Number(
                                                    bill.total ?? 0,
                                                  ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                                                  {bill.currency_symbol ??
                                                    currencySymbol}{" "}
                                                  {Number(
                                                    bill.balance ?? 0,
                                                  ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                                                  {bill.currency_symbol ??
                                                    currencySymbol}{" "}
                                                  {Number(
                                                    bill.amount_applied ?? 0,
                                                  ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}
                                                </td>
                                                <td className="px-2 py-1.5 text-body text-subtext-color">
                                                  {bill.status ?? "—"}
                                                </td>
                                              </tr>
                                            ),
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="flex w-full items-center justify-between px-3">
                              <span className="text-body font-body text-subtext-color">
                                Total
                              </span>
                              <span className="text-body-bold font-body-bold text-default-font">
                                {currencySymbol}{" "}
                                {Number(totalAmount).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    {/* Split Deposit */}
                    {(rawJson.category_type === "split_deposit" ||
                      rawJson.category_type === "deposit_split") &&
                      rawJson.category_details?.splits?.length > 0 && (
                        <>
                          <div className="flex w-full flex-col gap-2">
                            <span className="text-heading-4 font-heading-4 text-default-font">
                              Deposit From Other Accounts (Split)
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              Split across{" "}
                              {rawJson.category_details.split_count ??
                                rawJson.category_details.splits.length}{" "}
                              sources
                            </span>
                          </div>
                          <div className="flex w-full flex-col gap-2">
                            {(rawJson.category_details.splits || []).map(
                              (split: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex w-full items-center justify-between px-3 py-2 rounded border border-neutral-border bg-white"
                                >
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {split.from_account_name ||
                                      `Account ${index + 1}`}
                                  </span>
                                  <div className="flex flex-col items-end gap-1">
                                    <span className="text-body-bold font-body-bold text-success-600">
                                      {rawJson.currency_symbol ?? "₹"}{" "}
                                      {Number(split.amount ?? 0).toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </span>
                                    {split.payment_mode && (
                                      <span className="text-caption font-caption text-subtext-color">
                                        {split.payment_mode}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                          {rawJson.category_details.total_amount != null && (
                            <div className="flex w-full items-center justify-between px-3">
                              <span className="text-body font-body text-subtext-color">
                                Total
                              </span>
                              <span className="text-body-bold font-body-bold text-default-font">
                                {rawJson.currency_symbol ?? "₹"}{" "}
                                {Number(
                                  rawJson.category_details.total_amount,
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    {/* Split Transfer */}
                    {(rawJson.category_type === "split_transfer" ||
                      rawJson.category_type ===
                        "transfer_to_other_accounts_split") &&
                      rawJson.category_details?.splits?.length > 0 && (
                        <>
                          <div className="flex w-full flex-col gap-2">
                            <span className="text-heading-4 font-heading-4 text-default-font">
                              Transfer to Other Accounts (Split)
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              Split across{" "}
                              {rawJson.category_details.split_count ??
                                rawJson.category_details.splits.length}{" "}
                              destinations
                            </span>
                          </div>
                          <div className="flex w-full flex-col gap-2">
                            {(rawJson.category_details.splits || []).map(
                              (split: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex w-full items-center justify-between px-3 py-2 rounded border border-neutral-border bg-white"
                                >
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {split.to_account_name ||
                                      split.account ||
                                      `Account ${index + 1}`}
                                  </span>
                                  <div className="flex flex-col items-end gap-1">
                                    <span className="text-body-bold font-body-bold text-success-600">
                                      {rawJson.currency_symbol ?? "₹"}{" "}
                                      {Number(split.amount ?? 0).toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </span>
                                    {split.payment_mode && (
                                      <span className="text-caption font-caption text-subtext-color">
                                        {split.payment_mode}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                          {rawJson.category_details.total_amount != null && (
                            <div className="flex w-full items-center justify-between px-3">
                              <span className="text-body font-body text-subtext-color">
                                Total
                              </span>
                              <span className="text-body-bold font-body-bold text-default-font">
                                {rawJson.currency_symbol ?? "₹"}{" "}
                                {Number(
                                  rawJson.category_details.total_amount,
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    {/* Other Category Types */}
                    {rawJson.category_type &&
                      rawJson.category_type !== "customer_payment" &&
                      rawJson.category_type !== "vendor_payment" &&
                      rawJson.category_type !== "split_customer" &&
                      rawJson.category_type !== "split_vendor" &&
                      rawJson.category_type !== "split_deposit" &&
                      rawJson.category_type !== "split_transfer" &&
                      rawJson.category_type !== "deposit_split" &&
                      rawJson.category_type !==
                        "transfer_to_other_accounts_split" && (
                        <div className="flex w-full flex-col items-start gap-2">
                          {/* Transfer From Another Account */}
                          {rawJson.category_type ===
                            "transfer_from_another_account" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Transfer From Another Account
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  From Account
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {rawJson.from_account_id ||
                                    rawJson.category_details
                                      ?.from_account_name ||
                                    "N/A"}
                                </span>
                              </div>
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Interest Income */}
                          {rawJson.category_type === "interest_income" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Interest Income
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Other Income */}
                          {rawJson.category_type === "other_income" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Other Income
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.from_account_name ||
                                rawJson.from_account_id) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    From Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.from_account_name ||
                                      rawJson.from_account_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Deposit From Other Accounts */}
                          {rawJson.category_type ===
                            "deposit_from_other_accounts" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Deposit From Other Accounts
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.from_account_name ||
                                rawJson.from_account_id) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    From Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.from_account_name ||
                                      rawJson.from_account_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.customer_name ||
                                rawJson.customer_id) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Received From
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.customer_name ||
                                      rawJson.customer_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Deposit */}
                          {rawJson.category_type === "deposit" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Deposit from Another Account
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.from_account_name ||
                                rawJson.from_account_id) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    From Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.from_account_name ||
                                      rawJson.from_account_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Owners Contribution */}
                          {rawJson.category_type === "owners_contribution" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Owner's Contribution
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.from_account_name ||
                                rawJson.from_account_id) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    From Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.from_account_name ||
                                      rawJson.from_account_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Customer Expense Refund */}
                          {rawJson.category_type ===
                            "customer_expense_refund" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Customer Expense Refund
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {rawJson.from_account_id && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Expense GL
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.from_account_id}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Vendor Payment Refund */}
                          {rawJson.category_type ===
                            "vendor_payment_refund" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Vendor Payment Refund
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.vendor_name ||
                                rawJson.vendor_name) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Vendor
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.vendor_name ||
                                      rawJson.vendor_name ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Credits or Refunds Received */}
                          {rawJson.category_type ===
                            "credits_or_refund_received" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Credits or Refunds Received
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(rawJson.date) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Received Via
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {rawJson.payment_mode || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  From Account
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {rawJson.from_account_name ||
                                    rawJson.from_account_id ||
                                    "N/A"}
                                </span>
                              </div>
                              {(rawJson.vendor_name || rawJson.vendor_id) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Received From
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.vendor_name ||
                                      rawJson.vendor_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {rawJson.reference_number && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Transfer To Another Account */}
                          {rawJson.category_type ===
                            "transfer_to_another_account" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Transfer To Another Account
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {rawJson.to_account_id && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    To Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.to_account_id}
                                  </span>
                                </div>
                              )}
                              {rawJson.reference_number && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Transfer To Other Accounts */}
                          {rawJson.category_type ===
                            "transfer_to_other_accounts" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Transfer To Other Accounts
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {rawJson.to_account_id && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    To Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.to_account_id}
                                  </span>
                                </div>
                              )}
                              {rawJson.payment_mode && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {rawJson.reference_number && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Owner Drawing */}
                          {rawJson.category_type === "owner_drawing" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Owner Drawing
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {rawJson.to_account_id && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    To Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.to_account_id}
                                  </span>
                                </div>
                              )}
                              {rawJson.payment_mode && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {rawJson.reference_number && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Customer Payment Refund */}
                          {rawJson.category_type ===
                            "customer_payment_refund" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Customer Payment Refund
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {(rawJson.category_details?.customer_name ||
                                rawJson.customer_name) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Customer
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.customer_name ||
                                      rawJson.customer_name ||
                                      "N/A"}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.payment_mode ||
                                rawJson.payment_mode) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Payment Mode
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details?.payment_mode ||
                                      rawJson.payment_mode}
                                  </span>
                                </div>
                              )}
                              {(rawJson.category_details?.reference_number ||
                                rawJson.reference_number) && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.category_details
                                      ?.reference_number ||
                                      rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Card Payment */}
                          {rawJson.category_type === "card_payment" && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                Card Payment
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {rawJson.to_account_id && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    To Account (Credit Card)
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.to_account_id}
                                  </span>
                                </div>
                              )}
                              {rawJson.reference_number && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Reference Number:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.reference_number}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* Vendor Expense */}
                          {(rawJson.category_type === "vendor_expense" ||
                            rawJson.category_type === "expense") && (
                            <>
                              <span className="text-heading-4 font-heading-4 text-default-font">
                                {rawJson.category_type === "vendor_expense"
                                  ? "Vendor Expense"
                                  : "Expense"}
                              </span>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Date
                                </span>
                                <span className="text-body font-body text-default-font">
                                  {formatDate(
                                    rawJson.date ||
                                      rawJson.transaction_date ||
                                      rawJson.category_details?.date,
                                  ) || "N/A"}
                                </span>
                              </div>
                              <div className="flex w-full items-center justify-between">
                                <span className="text-body font-body text-subtext-color">
                                  Transaction Amount
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {rawJson.currency_symbol ||
                                    rawJson.category_details?.currency_symbol ||
                                    "₹"}{" "}
                                  {Number(
                                    rawJson.amount ||
                                      rawJson.transaction_amount ||
                                      0,
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              {rawJson.expense_account_name && (
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    Expense Account
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {rawJson.expense_account_name || "N/A"}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}
            {approval.entityType === "month_end_checklist_item" && (
              <>
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {approval.itemTitle || "Checklist Item"}
                  </span>
                  {approval.itemDescription && (
                    <span className="text-body font-body text-default-font">
                      {approval.itemDescription}
                    </span>
                  )}
                  {(() => {
                    const requestText =
                      approval.actionCode === "to_be_done_later"
                        ? "This request is to mark this checklist item as To be done later."
                        : approval.actionCode === "mark_as_done" ||
                            approval.actionCode === "marked_to_done" ||
                            approval.actionCode === "accountant_checklist"
                          ? "This request is to mark this checklist item as done."
                          : null;

                    return requestText ? (
                      <div className="flex w-full items-start rounded-md border border-solid border-info-200 bg-info-50 px-3 py-2">
                        <span className="text-body font-body text-info-700">
                          {requestText}
                        </span>
                      </div>
                    ) : null;
                  })()}
                </div>
                {approval.attachments && approval.attachments.length > 0 && (
                  <>
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                    <div className="flex w-full flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Attachments
                      </span>
                      <div className="flex w-full flex-col items-start gap-2">
                        {approval.attachments.map((attachment, index) => (
                          <div
                            key={attachment.id || index}
                            className="flex w-full items-center gap-3 rounded-md bg-neutral-50 px-3 py-3"
                          >
                            <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600" />
                            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                              <span className="text-body-bold font-body-bold text-default-font">
                                {attachment.originalFilename ||
                                  attachment.fileName ||
                                  "attachment"}
                              </span>
                              <span className="text-caption font-caption text-subtext-color">
                                {attachment.fileSize
                                  ? `${(attachment.fileSize / 1024).toFixed(2)} KB`
                                  : "Ready"}
                              </span>
                            </div>
                            <div className="flex flex-none items-center gap-1">
                              <IconButton
                                variant="neutral-tertiary"
                                size="small"
                                icon={<FeatherEye />}
                                onClick={() => {
                                  if (attachment.s3Url) {
                                    window.open(attachment.s3Url, "_blank");
                                  }
                                }}
                              />
                              <IconButton
                                variant="neutral-tertiary"
                                size="small"
                                icon={<FeatherDownload />}
                                onClick={() => {
                                  if (attachment.s3Url) {
                                    window.open(attachment.s3Url, "_blank");
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            {approval.entityType === "tenant_compliance_calendar" && (
              <>
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Compliance Details
                  </span>
                  <div className="flex w-full flex-col items-start gap-2">
                    {rawJson?.compliance_form_name && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Form Name
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.compliance_form_name}
                        </span>
                      </div>
                    )}
                    {rawJson?.period && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Period
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.period}
                        </span>
                      </div>
                    )}
                    {rawJson?.due_date && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Due Date
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatDate(rawJson.due_date)}
                        </span>
                      </div>
                    )}
                    {rawJson?.compliance_type_details?.authority && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Authority
                        </span>
                        <span className="text-body font-body text-default-font">
                          {rawJson.compliance_type_details.authority}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {complianceProofsLoading ? (
                  <>
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                    <div className="flex w-full flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Compliance Proofs
                      </span>
                      <div className="text-body font-body text-subtext-color">
                        Loading proofs...
                      </div>
                    </div>
                  </>
                ) : complianceProofs.length > 0 ? (
                  <>
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                    <div className="flex w-full flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Compliance Proofs ({complianceProofs.length})
                      </span>
                      <div className="flex w-full flex-col items-start gap-2">
                        {complianceProofs.map((proof, index) => (
                          <div
                            key={proof.id || index}
                            className="flex w-full items-center gap-3 rounded-md bg-neutral-50 px-3 py-3"
                          >
                            <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600" />
                            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                              <span className="text-body-bold font-body-bold text-default-font">
                                {proof.file_name || `Proof ${index + 1}`}
                              </span>
                              <span className="text-caption font-caption text-subtext-color">
                                {proof.file_size
                                  ? `${(proof.file_size / 1024).toFixed(2)} KB`
                                  : proof.proof_type || "Proof"}
                                {proof.is_primary && (
                                  <span className="ml-2 text-brand-600">
                                    (Primary)
                                  </span>
                                )}
                              </span>
                            </div>
                            <IconButton
                              variant="neutral-tertiary"
                              size="small"
                              icon={<FeatherDownload />}
                              onClick={() => {
                                if (proof.signed_url) {
                                  window.open(proof.signed_url, "_blank");
                                } else if (proof.file_url) {
                                  window.open(proof.file_url, "_blank");
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : rawJson?.proof_count === 0 ? (
                  <>
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                    <div className="flex w-full flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Compliance Proofs
                      </span>
                      <div className="text-body font-body text-subtext-color">
                        No proofs uploaded
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </>
        )}
        {/* Changes Requested moved near Raised by for invoice/bill */}
        {(approval.entityType === "invoice" ||
          approval.entityType === "bill") &&
          entityId && (
            <>
              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
              <div className="flex w-full flex-col items-start gap-3">
                <span className="text-body-bold font-body-bold text-default-font">
                  Document
                </span>
                <div className="flex w-full items-center justify-between gap-3 rounded-md bg-neutral-50 px-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600 flex-none" />
                    <div className="flex min-w-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font truncate">
                        {finalFileName || "document.pdf"}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        {finalDocumentLoading ? "Loading..." : "Ready"}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={() => {
                      const id = String(entityId);
                      const path =
                        approval.entityType === "invoice"
                          ? "/invoices/review"
                          : "/bills/review";
                      navigate(`${path}?documentId=${encodeURIComponent(id)}`, {
                        state: { documentId: id },
                      });
                    }}
                  >
                    Open in review
                  </Button>
                </div>
              </div>
            </>
          )}
        {(approval.entityType === "invoice" ||
          approval.entityType === "bill") &&
          !isCreateInvoiceOrBill &&
          !isInvoiceWriteOff && (
            <>
              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
              <ValidationChecks
                entityType={approval.entityType}
                invoiceJson={invoiceJsonData}
                billJson={billJsonData}
                invoiceDocument={invoiceDoc}
                billDocument={billDoc}
              />
            </>
          )}
        {isCreateInvoiceOrBill &&
          (() => {
            const attachmentUrls =
              (approval.entityType === "invoice"
                ? (invoiceDoc?.attachment_urls as Array<{
                    signed_url: string;
                    filename: string;
                    file_size?: number | null;
                  }>)
                : (billDoc?.attachment_urls as Array<{
                    signed_url: string;
                    filename: string;
                    file_size?: number | null;
                  }>)) || [];
            if (attachmentUrls.length === 0) return null;
            return (
              <>
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Attachments
                  </span>
                  <div className="flex w-full flex-col items-start gap-2">
                    {attachmentUrls.map((att, index) => (
                      <div
                        key={index}
                        className="flex w-full items-center gap-3 rounded-md bg-neutral-50 px-3 py-3"
                      >
                        <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600" />
                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {att.filename || "attachment"}
                          </span>
                          <span className="text-caption font-caption text-subtext-color">
                            {att.file_size != null
                              ? `${(att.file_size / 1024).toFixed(2)} KB`
                              : "Ready"}
                          </span>
                        </div>
                        <IconButton
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherDownload />}
                          onClick={() => {
                            if (att.signed_url) {
                              window.open(att.signed_url, "_blank");
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        {/* Display Document data for credit_note/debit_note (e.g. delete - reviewer sees original doc) */}
        {(approval.entityType === "credit_note" ||
          approval.entityType === "debit_note") &&
          otherChanges.length > 0 && (
            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                {actionTypes.includes("delete_after_push_to_zoho")
                  ? "Document data"
                  : "Changes"}
              </span>
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                {otherChanges.map((change, index) => (
                  <span
                    key={index}
                    className="text-body font-body text-default-font whitespace-pre-line"
                  >
                    {change}
                  </span>
                ))}
              </div>
            </div>
          )}
        {/* Display Customer/Vendor/GL bulk import summary */}
        {(approval.entityType === "customer" ||
          approval.entityType === "vendor" ||
          approval.entityType === "gl") && (
          <div className="flex w-full flex-col items-start gap-3">
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                {approval.entityType === "customer"
                  ? "Customer"
                  : approval.entityType === "vendor"
                    ? "Vendor"
                    : "GL"}{" "}
                Details
              </span>
              {isBulkUploadDetail && bulkImportReviewRows.length > 0 ? (
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => setBulkImportRowsModalOpen(true)}
                >
                  View import rows
                </Button>
              ) : null}
            </div>
            {otherChanges.length > 0 ? (
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                {otherChanges.map((change, index) => (
                  <span
                    key={index}
                    className="text-body font-body text-default-font whitespace-pre-line"
                  >
                    {change}
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                <span className="text-body font-body text-subtext-color">
                  No details available
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <BulkUploadReviewRowsModal
        open={bulkImportRowsModalOpen}
        onOpenChange={setBulkImportRowsModalOpen}
        entityType={
          approval.entityType === "vendor"
            ? "vendor"
            : approval.entityType === "gl"
              ? "gl"
              : "customer"
        }
        rows={bulkImportReviewRows}
      />
    </div>
  );
};
